'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BookUser,
  Download,
  Edit,
  Instagram,
  Loader2,
  MessageCircle,
  MessageSquare,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Send,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useDialog } from '@/components/providers/DialogProvider'
import { api, queryKeys } from '@/lib/api'
import { useNotificationActions } from '@/lib/notifications'
import { normalizeWhatsAppPhone } from '@/lib/phone'
import { useDebounce } from '@/hooks/use-api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  UnifiedContactForm,
  type ContactFormData,
} from '@/components/ui/unified-contact-form'
import type {
  FacebookConversationContact,
  MessageTemplate,
  MessageTemplatePayload,
  MessagingChannel,
} from '@/types'

interface TemplateFormState {
  name: string
  language: string
  description: string
  externalId: string
  body: string
  componentsJson: string
  category: string
  isActive: boolean
  submitToFacebook: boolean
  // New fields for enhanced template support
  headerType: 'text' | 'image' | 'video' | 'document' | 'location' | '' | null
  headerText: string
  headerMediaUrl: string
  bodyText: string
  footerText: string
  buttons: Array<{
    type:
      | 'quick_reply'
      | 'url'
      | 'phone_number'
      | 'copy_code'
      | 'otp'
      | 'flow'
      | 'catalog' // catalog for MPM/SPM
    title: string // Used for quick_reply, url, phone_number, otp (authentication)
    url?: string // Used for url type
    phone?: string // Used for phone_number type
    example?: string // Used for copy_code type (utility/marketing templates)
    otp_type?: 'copy_code' // Used for otp type (authentication templates)
    flow_id?: string
    flow_name?: string
    flow_json?: string
    flow_action?: string
    navigate_screen?: string
    icon?: string
    text?: string // Used for flow type
    payload?: string
    // Future: catalog support for MPM (Multi-Product) and SPM (Single-Product) buttons
  }>
  variables: Array<{
    key: string
    example: string
    type: 'positional' | 'named' // positional: {{1}}, named: {{name}}
    name?: string // Only used for named parameters
  }>
}

const defaultTemplateForm: TemplateFormState = {
  name: '',
  language: 'en_US',
  description: '',
  externalId: '',
  body: '',
  componentsJson: '[{"type":"BODY","text":"Hello, welcome to our service!"}]',
  category: 'UTILITY',
  isActive: true,
  submitToFacebook: true,
  // New default values
  headerType: null,
  headerText: '',
  headerMediaUrl: '',
  bodyText: 'Hello, welcome to our service!',
  footerText: '',
  buttons: [],
  variables: [],
}

interface OutboundMessagingProps {
  isSupervisor: boolean
}

interface WhatsappContactSummary {
  id: number
  firstName?: string | null
  lastName?: string | null
  title?: string | null
  phoneNumber?: string | number | null
  email?: string | null
  fbMessengerId?: string | null
  telegramId?: string | null
  telegramUserId?: string | null
  instagramId?: string | null
  weChatId?: string | null
  lineId?: string | null
}

interface ContactSearchResult {
  contacts: WhatsappContactSummary[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
    hasMore: boolean
  }
}

type ContactPresenceField =
  | 'phoneNumber'
  | 'fbMessengerId'
  | 'telegramId'
  | 'telegramUserId'
  | 'instagramId'
  | 'weChatId'
  | 'lineId'

type ContactFilterKey =
  | 'hasPhone'
  | 'hasMessenger'
  | 'hasTelegram'
  | 'hasInstagram'
  | 'hasWeChat'
  | 'hasLine'

interface ContactFilterConfig {
  key: ContactFilterKey
  label: string
  field: ContactPresenceField
  icon: LucideIcon
  badgeStyles: string
}

const CONTACT_FILTERS: ContactFilterConfig[] = [
  {
    key: 'hasPhone',
    label: 'WhatsApp',
    field: 'phoneNumber',
    icon: Phone,
    badgeStyles: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  {
    key: 'hasMessenger',
    label: 'Messenger',
    field: 'fbMessengerId',
    icon: MessageCircle,
    badgeStyles: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  {
    key: 'hasTelegram',
    label: 'Telegram',
    field: 'telegramId',
    icon: Send,
    badgeStyles: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  {
    key: 'hasInstagram',
    label: 'Instagram',
    field: 'instagramId',
    icon: Instagram,
    badgeStyles: 'bg-rose-50 text-rose-700 border-rose-200',
  },
  {
    key: 'hasWeChat',
    label: 'WeChat',
    field: 'weChatId',
    icon: MessageCircle,
    badgeStyles: 'bg-lime-50 text-lime-700 border-lime-200',
  },
  {
    key: 'hasLine',
    label: 'LINE',
    field: 'lineId',
    icon: MessageSquare,
    badgeStyles: 'bg-green-50 text-green-700 border-green-200',
  },
]

interface ContactFormValues {
  firstName: string
  lastName: string
  title: string
  email: string
  phoneNumber: string
  fbMessengerId: string
  telegramId: string
  telegramUserId: string
  instagramId: string
  weChatId: string
  lineId: string
}

const CONTACT_FIELD_LABEL_MAP: Array<{
  field: keyof ContactFormValues
  label: string
}> = [
  { field: 'phoneNumber', label: 'Phone number' },
  { field: 'fbMessengerId', label: 'Facebook Messenger ID' },
  { field: 'telegramId', label: 'Telegram handle' },
  { field: 'telegramUserId', label: 'Telegram user ID' },
  { field: 'instagramId', label: 'Instagram handle' },
  { field: 'weChatId', label: 'WeChat ID' },
  { field: 'lineId', label: 'LINE ID' },
]

const defaultContactFormValues: ContactFormValues = {
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phoneNumber: '',
  fbMessengerId: '',
  telegramId: '',
  telegramUserId: '',
  instagramId: '',
  weChatId: '',
  lineId: '',
}

export function OutboundMessaging({ isSupervisor }: OutboundMessagingProps) {
  const { showError, showWarning, showSuccess } = useNotificationActions()
  const { confirm } = useDialog()

  // Helper function to map channel label to MessagingChannel type
  const mapChannelLabelToType = (label: string): MessagingChannel => {
    const normalized = label.toLowerCase().trim()

    // Map based on label or channel name
    switch (normalized) {
      case 'whatsapp':
        return 'whatsapp'
      case 'messenger':
      case 'facebook':
        return 'facebook_messenger'
      case 'telegram':
        return 'telegram' as MessagingChannel
      case 'instagram':
        return 'instagram' as MessagingChannel
      case 'wechat':
        return 'wechat' as MessagingChannel
      case 'line':
        return 'line' as MessagingChannel
      default:
        // Default to whatsapp as fallback
        return 'whatsapp'
    }
  }

  // Helper function to render WhatsApp formatting
  const renderWhatsAppFormatting = (text: string) => {
    if (!text) return null

    const parts: React.ReactNode[] = []
    let lastIndex = 0

    // Regex to match WhatsApp formatting: *bold*, _italic_, ~strikethrough~, `monospace`
    const regex = /\*(.*?)\*|_(.*?)_|~(.*?)~|`(.*?)`/g
    let match

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index))
      }

      // Add formatted text
      if (match[1] !== undefined) {
        // Bold
        parts.push(<strong key={match.index}>{match[1]}</strong>)
      } else if (match[2] !== undefined) {
        // Italic
        parts.push(<em key={match.index}>{match[2]}</em>)
      } else if (match[3] !== undefined) {
        // Strikethrough
        parts.push(
          <span key={match.index} className="line-through">
            {match[3]}
          </span>
        )
      } else if (match[4] !== undefined) {
        // Monospace
        parts.push(
          <code
            key={match.index}
            className="font-mono bg-gray-100 px-1 rounded"
          >
            {match[4]}
          </code>
        )
      }

      lastIndex = regex.lastIndex
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts.length > 0 ? parts : text
  }

  const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // Debug logging for OutboundMessaging
  // console.log('OutboundMessaging Debug:', { isSupervisor })
  const [channel, setChannel] = useState<MessagingChannel>('whatsapp')
  const [recipient, setRecipient] = useState('')
  const [message, setMessage] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('')
  const [templateParameters, setTemplateParameters] = useState<string[]>([])
  const queryClient = useQueryClient()
  const [isSending, setIsSending] = useState(false)
  const [templateForm, setTemplateForm] =
    useState<TemplateFormState>(defaultTemplateForm)
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(
    null
  )
  const [showTemplateForm, setShowTemplateForm] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [whatsappTemplates, setWhatsappTemplates] = useState<any[]>([])
  const [fetchingTemplates, setFetchingTemplates] = useState(false)
  const [importingTemplates, setImportingTemplates] = useState<Set<string>>(
    new Set()
  )
  const [hasCustomComponentsJson, setHasCustomComponentsJson] = useState(false)
  // Contact search state (for all social media channels)
  const [showContactSuggestions, setShowContactSuggestions] = useState(false)
  const [selectedContact, setSelectedContact] =
    useState<WhatsappContactSummary | null>(null)
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false)
  const [contactSearchTerm, setContactSearchTerm] = useState('')
  const debouncedContactSearch = useDebounce(contactSearchTerm, 300)
  const [contactFilters, setContactFilters] = useState<
    Record<ContactFilterKey, boolean>
  >({
    hasPhone: false,
    hasMessenger: false,
    hasTelegram: false,
    hasInstagram: false,
    hasWeChat: false,
    hasLine: false,
  })
  const [contactFormMode, setContactFormMode] = useState<
    'list' | 'create' | 'edit'
  >('list')
  const [contactFormValues, setContactFormValues] = useState<ContactFormValues>(
    defaultContactFormValues
  )
  const [contactFormErrors, setContactFormErrors] = useState<
    Partial<Record<keyof ContactFormValues, string>>
  >({})
  const [contactFormSubmissionError, setContactFormSubmissionError] = useState<
    string | null
  >(null)
  const [contactBeingEdited, setContactBeingEdited] =
    useState<WhatsappContactSummary | null>(null)
  const [selectedChannels, setSelectedChannels] = useState<
    Record<ContactPresenceField, boolean>
  >({
    phoneNumber: false,
    fbMessengerId: false,
    telegramId: false,
    telegramUserId: false,
    instagramId: false,
    weChatId: false,
    lineId: false,
  })
  const [assignToMe, setAssignToMe] = useState(false)
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)

  const applyContactFormSubmissionError = useCallback(
    (message: string | null) => {
      if (!message) {
        setContactFormSubmissionError(null)
        return
      }

      setContactFormSubmissionError(message)

      const matchedField = CONTACT_FIELD_LABEL_MAP.find(entry =>
        message.toLowerCase().includes(entry.label.toLowerCase())
      )

      if (matchedField) {
        setContactFormErrors(prev => ({
          ...prev,
          [matchedField.field]: message,
        }))
      }
    },
    [setContactFormErrors, setContactFormSubmissionError]
  )

  const contactHasField = (
    contact: WhatsappContactSummary,
    field: ContactPresenceField
  ): boolean => {
    const value = contact[field]
    if (value === null || value === undefined) {
      return false
    }
    const trimmed = String(value).trim()
    return trimmed.length > 0
  }

  const getContactDisplayName = (contact: WhatsappContactSummary): string => {
    const first =
      typeof contact.firstName === 'string' ? contact.firstName.trim() : ''
    const last =
      typeof contact.lastName === 'string' ? contact.lastName.trim() : ''
    const parts = [first, last].filter(Boolean)
    if (parts.length > 0) {
      return parts.join(' ')
    }
    const phone = contact.phoneNumber
    if (phone !== null && phone !== undefined) {
      const phoneString = String(phone).trim()
      if (phoneString) {
        return phoneString
      }
    }
    return 'Unnamed contact'
  }

  const toggleContactFilter = (key: ContactFilterKey) => {
    setContactFilters(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phoneRegex = /^\d{7,15}$/

  const resetContactForm = useCallback(() => {
    setContactFormValues(defaultContactFormValues)
    setContactFormErrors({})
    setContactBeingEdited(null)
    setContactFormMode('list')
    applyContactFormSubmissionError(null)
  }, [
    setContactFormValues,
    setContactFormErrors,
    setContactBeingEdited,
    setContactFormMode,
    applyContactFormSubmissionError,
  ])

  const sanitizeFormValue = (value: string): string | undefined => {
    const trimmed = value?.trim() ?? ''
    return trimmed.length > 0 ? trimmed : undefined
  }

  const normalizeRecipientForChannel = (
    value: string,
    channelName: string
  ): string => {
    if (channelName !== 'whatsapp') {
      return value.trim()
    }
    return normalizeWhatsAppPhone(value)
  }

  const startCreateContact = () => {
    setContactFormValues(prev => ({
      ...defaultContactFormValues,
      phoneNumber: recipient.trim() || prev.phoneNumber,
    }))
    setContactFormErrors({})
    setContactBeingEdited(null)
    setContactFormMode('create')
    applyContactFormSubmissionError(null)
  }

  const startEditContact = (contact: WhatsappContactSummary) => {
    setContactFormValues({
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      title: contact.title ?? '',
      email: contact.email ?? '',
      phoneNumber: contact.phoneNumber ? String(contact.phoneNumber) : '',
      fbMessengerId: contact.fbMessengerId ?? '',
      telegramId: contact.telegramId ?? '', // Clear this field since we're using telegramUserId in the form
      telegramUserId: contact.telegramUserId ?? contact.telegramId ?? '',
      instagramId: contact.instagramId ?? '',
      weChatId: contact.weChatId ?? '',
      lineId: contact.lineId ?? '',
    })
    setContactFormErrors({})
    setContactBeingEdited(contact)
    setContactFormMode('edit')
    applyContactFormSubmissionError(null)
  }

  const validateContactForm = (values: ContactFormValues): boolean => {
    const errors: Partial<Record<keyof ContactFormValues, string>> = {}

    if (!values.firstName.trim()) {
      errors.firstName = 'First name is required'
    }

    if (values.email.trim()) {
      if (!emailRegex.test(values.email.trim())) {
        errors.email = 'Invalid email format'
      }
    }

    if (values.phoneNumber.trim()) {
      const normalizedPhone = normalizeWhatsAppPhone(values.phoneNumber)
      if (!phoneRegex.test(normalizedPhone)) {
        errors.phoneNumber = 'Use country code without symbols (min 7 digits)'
      }
    }

    setContactFormErrors(errors)
    const hasErrors = Object.keys(errors).length > 0
    if (hasErrors) {
      setContactFormSubmissionError(
        'Please correct the highlighted fields before saving.'
      )
    } else {
      setContactFormSubmissionError(null)
    }
    return !hasErrors
  }

  const buildContactPayload = (values: ContactFormValues) => ({
    firstName: values.firstName.trim(),
    lastName: sanitizeFormValue(values.lastName),
    title: sanitizeFormValue(values.title),
    email: sanitizeFormValue(values.email),
    phoneNumber: sanitizeFormValue(normalizeWhatsAppPhone(values.phoneNumber)),
    fbMessengerId: sanitizeFormValue(values.fbMessengerId),
    telegramId: sanitizeFormValue(values.telegramId), // Still include for backward compatibility
    telegramUserId: sanitizeFormValue(values.telegramUserId),
    instagramId: sanitizeFormValue(values.instagramId),
    weChatId: sanitizeFormValue(values.weChatId),
    lineId: sanitizeFormValue(values.lineId),
  })

  const messageTemplatesRootKey = useMemo(
    () => ['sessions', 'message-templates'] as const,
    []
  )

  const messageTemplatesKey = useMemo(
    () => [...messageTemplatesRootKey, channel] as const,
    [channel, messageTemplatesRootKey]
  )

  const {
    data: templatesData,
    isFetching: loadingTemplates,
    error: templatesError,
  } = useQuery({
    queryKey: messageTemplatesKey,
    enabled: channel === 'whatsapp',
    queryFn: async () => {
      const response = await api.sessions.getMessageTemplates(channel)
      if (!response.success || !Array.isArray(response.data)) {
        throw new Error(response.error || 'Unable to load message templates.')
      }
      return response.data as MessageTemplate[]
    },
  })

  const {
    data: contactsData,
    isFetching: loadingContacts,
    error: contactsError,
  } = useQuery({
    queryKey: ['sessions', 'facebook-contacts'] as const,
    enabled: channel === 'facebook_messenger',
    queryFn: async () => {
      const response = await api.sessions.getFacebookContacts()
      if (!response.success || !Array.isArray(response.data)) {
        throw new Error(response.error || 'Unable to load Facebook contacts.')
      }
      return response.data as FacebookConversationContact[]
    },
  })

  // Fetch current agent's teams
  const {
    data: agentTeamsData,
    isFetching: loadingAgentTeams,
    error: agentTeamsError,
  } = useQuery({
    queryKey: ['agent', 'teams'] as const,
    queryFn: async () => {
      const response = await api.teams.getMyTeams()
      if (!response.success || !Array.isArray(response.data)) {
        return []
      }
      return response.data as Array<{ id: number; name: string }>
    },
  })

  const agentTeams = useMemo(() => {
    return agentTeamsData ?? []
  }, [agentTeamsData])

  // Auto-select team if agent has only one team
  useEffect(() => {
    if (assignToMe && agentTeams.length === 1 && selectedTeamId === null) {
      setSelectedTeamId(agentTeams[0].id)
    }
  }, [assignToMe, agentTeams, selectedTeamId])

  const templates = useMemo(() => {
    if (channel !== 'whatsapp') {
      return []
    }
    return templatesData ?? []
  }, [channel, templatesData])

  const contacts = useMemo(() => {
    if (channel !== 'facebook_messenger') {
      return []
    }
    return contactsData ?? []
  }, [channel, contactsData])

  const normalizedRecipient = recipient.trim()
  const normalizedAddressBookSearch = debouncedContactSearch.trim()

  // Contact search results (sessions/contacts)
  const { data: contactSearchData, isFetching: searchingContacts } =
    useQuery<ContactSearchResult>({
      queryKey: [
        ...queryKeys.agentContacts.all,
        'search',
        normalizedRecipient,
      ] as const,
      enabled: normalizedRecipient.length >= 2,
      queryFn: async () => {
        const res = await api.sessions.searchContacts({
          search: normalizedRecipient,
          limit: 5,
        })
        if (!res.success)
          throw new Error(res.error || 'Failed to search contacts')
        return res.data as ContactSearchResult
      },
    })

  const {
    data: addressBookData,
    isFetching: loadingAddressBook,
    error: addressBookError,
  } = useQuery<ContactSearchResult>({
    queryKey: [
      ...queryKeys.agentContacts.all,
      'address-book',
      normalizedAddressBookSearch,
      'all', // Always fetch all contacts, filter client-side
    ] as const,
    enabled: isContactDialogOpen,
    queryFn: async () => {
      const res = await api.sessions.searchContacts({
        search: normalizedAddressBookSearch
          ? normalizedAddressBookSearch
          : undefined,
        page: 1,
        limit: 50,
        withPhoneOnly: false, // CRITICAL: Fetch ALL contacts, not just phone numbers
      })
      if (!res.success) {
        throw new Error(res.error || 'Failed to load contacts')
      }
      return res.data as ContactSearchResult
    },
  })

  const filteredAddressBookContacts = useMemo(() => {
    const base = (addressBookData?.contacts as WhatsappContactSummary[]) ?? []

    // Get list of active filters
    const activeFilters = CONTACT_FILTERS.filter(f => contactFilters[f.key])

    // If no filters active, show all contacts
    if (activeFilters.length === 0) {
      return base
    }

    // Show contacts that have ANY of the selected channels (OR logic)
    return base.filter((contact: WhatsappContactSummary) =>
      activeFilters.some(filter => contactHasField(contact, filter.field))
    )
  }, [addressBookData, contactFilters])
  // Keep selectedContact in sync when recipient matches a known contact
  useEffect(() => {
    const contactId = recipient.trim()
    if (!contactId) {
      setSelectedContact(null)
      return
    }

    // Combine both search results AND address book contacts
    const searchList =
      (contactSearchData?.contacts as WhatsappContactSummary[]) ?? []
    const addressList =
      (addressBookData?.contacts as WhatsappContactSummary[]) ?? []
    const allContacts = [...searchList, ...addressList]

    // Try to match by phone number or any social media ID
    const exact =
      allContacts.find(
        (contact: WhatsappContactSummary) =>
          String(contact.phoneNumber ?? '') === contactId ||
          String(contact.fbMessengerId ?? '') === contactId ||
          String(contact.telegramUserId ?? '') === contactId ||
          String(contact.telegramId ?? '') === contactId ||
          String(contact.instagramId ?? '') === contactId ||
          String(contact.weChatId ?? '') === contactId ||
          String(contact.lineId ?? '') === contactId
      ) || null
    setSelectedContact(exact)
  }, [recipient, contactSearchData, addressBookData])
  // Auto-fill recipient when a template is selected (if we already know the contact)
  useEffect(() => {
    const hasTemplate =
      typeof selectedTemplateId === 'number' && selectedTemplateId > 0
    if (!hasTemplate) return
    if (recipient.trim()) return // don't override manual input

    // Try to get contact ID from selected contact or single search result
    const contactIdFromSelected =
      selectedContact?.phoneNumber ||
      selectedContact?.fbMessengerId ||
      selectedContact?.telegramId ||
      selectedContact?.instagramId ||
      selectedContact?.weChatId ||
      selectedContact?.lineId

    const contactIdFromSingleResult =
      (contactSearchData?.contacts as WhatsappContactSummary[])?.length === 1
        ? (contactSearchData?.contacts as WhatsappContactSummary[])[0]
            ?.phoneNumber ||
          (contactSearchData?.contacts as WhatsappContactSummary[])[0]
            ?.fbMessengerId ||
          (contactSearchData?.contacts as WhatsappContactSummary[])[0]
            ?.telegramId ||
          (contactSearchData?.contacts as WhatsappContactSummary[])[0]
            ?.instagramId ||
          (contactSearchData?.contacts as WhatsappContactSummary[])[0]
            ?.weChatId ||
          (contactSearchData?.contacts as WhatsappContactSummary[])[0]?.lineId
        : undefined

    const contactId = contactIdFromSelected || contactIdFromSingleResult
    if (contactId) setRecipient(String(contactId))
  }, [selectedTemplateId, selectedContact, contactSearchData, recipient])

  const selectedTemplate = useMemo(() => {
    if (typeof selectedTemplateId !== 'number') {
      return undefined
    }
    return templates.find(template => template.id === selectedTemplateId)
  }, [selectedTemplateId, templates])

  // Extract placeholder count from template
  const templatePlaceholderCount = useMemo(() => {
    if (!selectedTemplate) return 0

    // Find body component (case-insensitive)
    const bodyComponent = Array.isArray(selectedTemplate.components)
      ? selectedTemplate.components.find(
          (c: any) => c.type?.toLowerCase() === 'body'
        )
      : null

    if (!bodyComponent || !bodyComponent.text) return 0

    // Extract placeholders like {{1}}, {{2}}, etc.
    const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g) || []
    return matches.length
  }, [selectedTemplate])

  // Update parameter array when template changes
  useEffect(() => {
    if (templatePlaceholderCount > 0) {
      setTemplateParameters(new Array(templatePlaceholderCount).fill(''))
    } else {
      setTemplateParameters([])
    }
  }, [templatePlaceholderCount])
  // Auto-fill template parameters from selected contact (non-destructive)
  useEffect(() => {
    if (!selectedContact) return
    if (templatePlaceholderCount <= 0) return
    // Only fill if current params are all empty (avoid overriding user input)
    if (
      !templateParameters ||
      templateParameters.length !== templatePlaceholderCount
    )
      return
    const allEmpty = templateParameters.every(v => !v || v.trim() === '')
    if (!allEmpty) return

    const fullName =
      `${selectedContact.firstName ?? ''} ${selectedContact.lastName ?? ''}`.trim()
    const contactId =
      selectedContact.phoneNumber ||
      selectedContact.fbMessengerId ||
      selectedContact.telegramId ||
      selectedContact.instagramId ||
      selectedContact.weChatId ||
      selectedContact.lineId
    const contactIdString = contactId ? String(contactId) : ''

    const seed: string[] = []
    if (fullName) seed.push(fullName)
    if (contactIdString) seed.push(contactIdString)
    const values = seed.slice(0, templatePlaceholderCount)
    while (values.length < templatePlaceholderCount) values.push('')

    // Guard: do nothing if we have no values to fill or values already match
    const hasAny = values.some(v => v && v.trim() !== '')
    if (!hasAny) return
    if (JSON.stringify(values) === JSON.stringify(templateParameters)) return

    setTemplateParameters(values)
  }, [selectedContact, templatePlaceholderCount, templateParameters])

  useEffect(() => {
    if (templatesError instanceof Error) {
      console.error('Failed to load message templates:', templatesError)
      showError(
        'Template Load Failed',
        templatesError.message || 'Unable to load message templates.'
      )
    }
  }, [showError, templatesError])

  useEffect(() => {
    if (contactsError instanceof Error) {
      console.error('Failed to load Facebook contacts:', contactsError)
      showError(
        'Contact Load Failed',
        contactsError.message || 'Unable to load Facebook contacts.'
      )
    }
  }, [showError, contactsError])

  useEffect(() => {
    if (!isContactDialogOpen) {
      setContactSearchTerm('')
      resetContactForm()
    }
  }, [isContactDialogOpen, resetContactForm])

  const handleFetchWhatsAppTemplates = async () => {
    try {
      setFetchingTemplates(true)
      const response = await api.sessions.fetchWhatsAppTemplates()

      if (!response.success || !Array.isArray(response.data)) {
        throw new Error(response.error || 'Failed to fetch templates')
      }

      setWhatsappTemplates(response.data)
      setShowSyncModal(true)
      showSuccess(
        'Templates Fetched',
        `Found ${response.data.length} templates from Meta Business`
      )
    } catch (error: any) {
      console.error('Failed to fetch templates:', error)
      showError(
        'Fetch Failed',
        error?.message || 'Unable to fetch templates from Meta Business'
      )
    } finally {
      setFetchingTemplates(false)
    }
  }

  const handleImportTemplate = async (whatsappTemplate: any) => {
    try {
      const templateName = whatsappTemplate.name
      setImportingTemplates(prev => new Set(prev).add(templateName))

      // Check if template already exists in database
      const existingTemplate = templates.find(
        t => t.externalId === templateName || t.name === templateName
      )

      // Extract body component
      const bodyComponent = whatsappTemplate.components?.find(
        (c: any) => c.type === 'BODY'
      )

      // Normalize category to match enum
      const normalizedCategory = whatsappTemplate.category?.toUpperCase()
      const validCategory = ['MARKETING', 'UTILITY', 'AUTHENTICATION'].includes(
        normalizedCategory
      )
        ? normalizedCategory
        : 'UTILITY'

      const templateData: any = {
        channel: 'whatsapp',
        // Don't update name for existing templates - name is for internal use only
        ...(existingTemplate ? {} : { name: templateName }),
        externalId: templateName,
        language: whatsappTemplate.language || 'en_US',
        category: validCategory,
        status: whatsappTemplate.status, // Store Facebook status: APPROVED, PENDING, REJECTED, etc.
        description: `Imported from Meta Business - ${whatsappTemplate.category || 'N/A'} - Status: ${whatsappTemplate.status}`,
        body: bodyComponent?.text || '',
        components:
          whatsappTemplate.components?.map((c: any) => {
            const component: any = {
              type: c.type, // Keep uppercase (BODY, HEADER, FOOTER, BUTTONS)
            }

            // Only include fields that exist
            if (c.text) component.text = c.text
            if (c.format) component.format = c.format
            if (c.buttons) component.buttons = c.buttons
            if (c.example?.body_text) component.example = c.example

            return component
          }) || [],
        isActive: whatsappTemplate.status === 'APPROVED',
      }

      let response

      console.log(
        '📋 Template data to send:',
        JSON.stringify(templateData, null, 2)
      )

      if (existingTemplate) {
        // Update existing template
        console.log('Updating existing template:', existingTemplate.id)
        response = await api.sessions.updateMessageTemplate(
          existingTemplate.id,
          templateData
        )

        if (!response.success) {
          throw new Error(response.error || 'Failed to update template')
        }

        showSuccess(
          'Template Updated',
          `Template "${templateName}" updated with status: ${whatsappTemplate.status}`
        )
      } else {
        // Create new template
        console.log('Creating new template:', templateName)
        response = await api.sessions.createMessageTemplate(templateData)

        if (!response.success) {
          throw new Error(response.error || 'Failed to import template')
        }

        showSuccess(
          'Template Imported',
          `Template "${templateName}" imported successfully`
        )
      }

      // Refresh templates list
      await queryClient.invalidateQueries({
        queryKey: messageTemplatesKey,
      })
    } catch (error: any) {
      console.error('Failed to import template:', error)
      showError('Import Failed', error?.message || 'Unable to import template')
    } finally {
      setImportingTemplates(prev => {
        const next = new Set(prev)
        next.delete(whatsappTemplate.name)
        return next
      })
    }
  }

  const handleSendMessage = async () => {
    if (!selectedContact && !recipient.trim()) {
      showWarning('Missing Recipient', 'Please add a recipient.')
      return
    }

    if (!selectedTemplateId || typeof selectedTemplateId !== 'number') {
      showWarning(
        'Template Required',
        'Select a message template before sending.'
      )
      return
    }

    // Validate team selection if assigning to me and agent has multiple teams
    if (assignToMe && agentTeams.length > 1 && !selectedTeamId) {
      showWarning(
        'Team Required',
        'Please select a team to assign this session to.'
      )
      return
    }

    // Check if at least one channel is selected when contact is selected
    if (selectedContact && Object.values(selectedChannels).every(v => !v)) {
      showWarning(
        'Channel Required',
        'Please select at least one social media channel to send the message to.'
      )
      return
    }

    try {
      setIsSending(true)

      // Collect selected channel IDs
      const selectedChannelIds: Array<{
        channel: string
        recipientId: string
      }> = []

      // Determine the primary channel to use
      let primaryChannel: MessagingChannel = channel // Default fallback

      if (selectedContact) {
        // Use selected channels from checkboxes
        const selectedFilters = CONTACT_FILTERS.filter(
          filter =>
            selectedChannels[filter.field] && selectedContact[filter.field]
        )

        // Process selected channels in the order they were selected, not the order in CONTACT_FILTERS
        selectedFilters.forEach(filter => {
          const channelName = filter.label.toLowerCase().replace(' ', '_')
          const contactValue = selectedContact[filter.field]
          const recipientId =
            contactValue === null || contactValue === undefined
              ? ''
              : normalizeRecipientForChannel(String(contactValue), channelName)

          if (!recipientId) {
            return
          }

          selectedChannelIds.push({
            channel: channelName,
            recipientId,
          })
        })

        // Set primary channel to the first selected channel using the mapping function
        if (selectedChannelIds.length > 0) {
          const firstChannelName = selectedChannelIds[0].channel
          primaryChannel = mapChannelLabelToType(firstChannelName)
        } else {
          // This should never happen due to validation above, but adding safety check
          showWarning(
            'Channel Required',
            'Please select at least one social media channel to send the message to.'
          )
          return
        }
      } else {
        // Fallback to single recipient (for manual entry)
        selectedChannelIds.push({
          channel: channel,
          recipientId: normalizeRecipientForChannel(recipient, channel),
        })
        primaryChannel = channel
      }

      if (selectedChannelIds.length === 0) {
        showWarning('Missing Recipient', 'Please add a recipient.')
        return
      }

      const primaryRecipientId = selectedChannelIds[0].recipientId.trim()

      const payload: {
        channel: MessagingChannel
        recipient: string
        templateId?: number
        message?: string
        parameters?: string[]
        selectedChannels?: Array<{ channel: string; recipientId: string }>
        assignToMe?: boolean
        teamId?: number
        mediaUrl?: string
        mediaType?: 'photo' | 'image' | 'video' | 'audio' | 'document'
      } = {
        channel: primaryChannel, // ✅ Use detected channel instead of hardcoded 'whatsapp'
        recipient: primaryRecipientId,
        // Include templateId for WhatsApp, message for other channels
        ...(primaryChannel === 'whatsapp' && {
          templateId: selectedTemplateId as number,
        }),
        ...((primaryChannel === 'facebook_messenger' ||
          primaryChannel === 'instagram' ||
          primaryChannel === 'telegram' ||
          primaryChannel === 'wechat' ||
          primaryChannel === 'line') && {
          // For text-based channels, extract body from template
          message: (() => {
            if (selectedTemplate?.body) {
              return selectedTemplate.body
            }
            // If no body field, try to extract from components
            if (Array.isArray(selectedTemplate?.components)) {
              const bodyComponent = selectedTemplate.components.find(
                (c: any) => c.type?.toLowerCase() === 'body'
              )
              if (bodyComponent?.text) {
                return bodyComponent.text
              }
            }
            return 'Hello!'
          })(),
        }),
        selectedChannels: selectedChannelIds,
        assignToMe: assignToMe, // Add the assignToMe flag
        ...(assignToMe && selectedTeamId && { teamId: selectedTeamId }), // Add teamId if selected
      }

      // Add parameters if template has placeholders
      if (templateParameters.length > 0) {
        // Filter out empty parameters
        const filledParameters = templateParameters.filter(p => p.trim())
        if (filledParameters.length > 0) {
          payload.parameters = filledParameters

          // For Facebook Messenger and other text-based channels, substitute parameters in the message
          if (
            (primaryChannel === 'facebook_messenger' ||
              primaryChannel === 'instagram' ||
              primaryChannel === 'telegram' ||
              primaryChannel === 'wechat' ||
              primaryChannel === 'line') &&
            payload.message
          ) {
            let messageWithParams = payload.message
            filledParameters.forEach((param, index) => {
              const placeholder = `{{${index + 1}}}`
              messageWithParams = messageWithParams.replace(
                new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
                param
              )
            })
            payload.message = messageWithParams
          }
        }
      }

      // Debug logging
      console.log(
        '📤 [DEBUG] Sending channel message:',
        JSON.stringify(payload, null, 2)
      )

      const response = await api.sessions.initiateChannelMessage(payload)
      console.log(
        '📤 [DEBUG] Channel message status:',
        response.success ? 'success' : 'failed'
      )
      if (!response.success) {
        console.error('❌ [DEBUG] Channel message error:', {
          success: response.success,
          status: response.status,
          error: response.error,
          message: response.message,
        })
      }
      if (!response.success) {
        throw new Error(response.error || 'Failed to send message')
      }

      showSuccess('Message Sent', 'Conversation started successfully.')
      setMessage('')
      setSelectedTemplateId('')
      setTemplateParameters([])
      // Reset channel selections
      setSelectedChannels({
        phoneNumber: false,
        fbMessengerId: false,
        telegramId: false,
        instagramId: false,
        weChatId: false,
        lineId: false,
      })
    } catch (error: any) {
      console.error('Failed to initiate channel message:', error)
      showError(
        'Send Failed',
        error?.message ?? 'Unable to send message. Please try again.'
      )
    } finally {
      setIsSending(false)
    }
  }

  const handleSelectContactFromAddressBook = (
    contact: WhatsappContactSummary
  ) => {
    // Try to get any available contact ID (phone, social media, etc.)
    const contactId =
      contact.phoneNumber ||
      contact.fbMessengerId ||
      contact.telegramUserId ||
      contact.telegramId ||
      contact.instagramId ||
      contact.weChatId ||
      contact.lineId

    const contactIdString =
      contactId === null || contactId === undefined
        ? ''
        : String(contactId).trim()

    if (!contactIdString) {
      showWarning(
        'Contact ID Missing',
        'This contact does not have any contact information saved yet.'
      )
      return
    }

    setRecipient(contactIdString)
    setSelectedContact(contact)
    setShowContactSuggestions(false)
    setContactSearchTerm('')
    setIsContactDialogOpen(false)
  }

  const invalidateContactQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.agentContacts.all,
    })
  }

  const createContactMutation = useMutation<
    WhatsappContactSummary,
    Error,
    ContactFormValues
  >({
    mutationFn: async (values: ContactFormValues) => {
      const payload = buildContactPayload(values)
      const response = await api.sessions.createContact(payload)
      if (!response.success) {
        throw new Error(response.error || 'Failed to create contact')
      }
      return response.data as WhatsappContactSummary
    },
    onSuccess: async contact => {
      await invalidateContactQueries()
      showSuccess('Contact Created', 'Contact saved successfully.')
      if (contact) {
        // Set recipient to any available contact ID
        const contactId =
          contact.phoneNumber ||
          contact.fbMessengerId ||
          contact.telegramUserId ||
          contact.telegramId ||
          contact.instagramId ||
          contact.weChatId ||
          contact.lineId
        const contactIdString = contactId ? String(contactId).trim() : ''
        if (contactIdString) {
          setRecipient(contactIdString)
        }
        setSelectedContact(contact)
      }
      resetContactForm()
    },
    onError: error => {
      console.error('Failed to create contact:', error)
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to create contact. Please try again.'
      applyContactFormSubmissionError(message)
      showError('Create Failed', message)
    },
  })

  const updateContactMutation = useMutation<
    WhatsappContactSummary,
    Error,
    { id: number; values: ContactFormValues }
  >({
    mutationFn: async ({
      id,
      values,
    }: {
      id: number
      values: ContactFormValues
    }) => {
      const payload = buildContactPayload(values)
      const response = await api.sessions.updateContact(id, payload)
      if (!response.success) {
        throw new Error(response.error || 'Failed to update contact')
      }
      return response.data as WhatsappContactSummary
    },
    onSuccess: async contact => {
      await invalidateContactQueries()
      showSuccess('Contact Updated', 'Contact updated successfully.')
      if (contact) {
        const phoneString = contact.phoneNumber
          ? String(contact.phoneNumber).trim()
          : ''
        if (phoneString) {
          setRecipient(phoneString)
        }
        setSelectedContact(contact)
      }
      resetContactForm()
    },
    onError: error => {
      console.error('Failed to update contact:', error)
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to update contact. Please try again.'
      applyContactFormSubmissionError(message)
      showError('Update Failed', message)
    },
  })

  const deleteContactMutation = useMutation<number, Error, number>({
    mutationFn: async (contactId: number) => {
      const response = await api.sessions.deleteContact(contactId)
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete contact')
      }
      return contactId
    },
    onSuccess: async contactId => {
      await invalidateContactQueries()
      showSuccess('Contact Deleted', 'Contact removed successfully.')
      if (selectedContact?.id === contactId) {
        setSelectedContact(null)
        setRecipient('')
      }
      resetContactForm()
    },
    onError: error => {
      console.error('Failed to delete contact:', error)
      showError(
        'Delete Failed',
        error instanceof Error
          ? error.message
          : 'Unable to delete contact. Please try again.'
      )
    },
  })

  const isSubmittingContact =
    createContactMutation.isPending || updateContactMutation.isPending

  const handleContactFormSubmit = async (submittedData: ContactFormData) => {
    const normalizedValues: ContactFormValues = {
      firstName: submittedData.firstName ?? '',
      lastName: submittedData.lastName ?? '',
      title: submittedData.title ?? '',
      email: submittedData.email ?? '',
      phoneNumber: submittedData.phoneNumber ?? '',
      fbMessengerId: submittedData.fbMessengerId ?? '',
      telegramId: submittedData.telegramId ?? '',
      telegramUserId: submittedData.telegramUserId ?? '',
      instagramId: submittedData.instagramId ?? '',
      weChatId: submittedData.weChatId ?? '',
      lineId: submittedData.lineId ?? '',
    }

    setContactFormErrors({})
    applyContactFormSubmissionError(null)
    setContactFormValues(normalizedValues)
    if (!validateContactForm(normalizedValues)) {
      return
    }

    if (contactFormMode === 'create') {
      try {
        await createContactMutation.mutateAsync(normalizedValues)
      } catch (error) {
        const fallbackMessage =
          error instanceof Error
            ? error.message
            : 'Unable to create contact. Please try again.'
        applyContactFormSubmissionError(fallbackMessage)
      }
      return
    }

    if (contactFormMode === 'edit' && contactBeingEdited) {
      try {
        await updateContactMutation.mutateAsync({
          id: contactBeingEdited.id,
          values: normalizedValues,
        })
      } catch (error) {
        const fallbackMessage =
          error instanceof Error
            ? error.message
            : 'Unable to update contact. Please try again.'
        applyContactFormSubmissionError(fallbackMessage)
      }
    }
  }

  const handleDeleteContact = async (contact: WhatsappContactSummary) => {
    if (!isSupervisor) {
      showWarning('Permission Denied', 'Only supervisors can delete contacts.')
      return
    }

    const confirmed = await confirm({
      title: 'Delete Contact',
      description: `Delete contact "${getContactDisplayName(contact)}"?`,
      confirmLabel: 'Delete',
    })
    if (!confirmed) {
      return
    }

    deleteContactMutation.mutate(contact.id)
  }

  const openTemplateForm = (template?: MessageTemplate) => {
    if (template) {
      // Parse existing components to populate the enhanced form
      let headerType:
        | 'text'
        | 'image'
        | 'video'
        | 'document'
        | 'location'
        | ''
        | null = null
      let headerText = ''
      let headerMediaUrl = ''
      let bodyText = ''
      let footerText = ''
      const buttons: TemplateFormState['buttons'] = []
      const variables: TemplateFormState['variables'] = []

      if (template.components && Array.isArray(template.components)) {
        template.components.forEach((component: any) => {
          switch (component.type?.toUpperCase()) {
            case 'HEADER':
              headerType = component.format?.toLowerCase() || null
              if (component.text) headerText = component.text
              // Read from header_handle (correct Facebook API format for media)
              if (component.example?.header_handle) {
                headerMediaUrl = component.example.header_handle[0] || ''
              } else if (component.example?.header_text) {
                // Fallback for text headers
                headerMediaUrl = component.example.header_text[0] || ''
              }
              break
            case 'BODY':
              bodyText = component.text || ''
              // Extract variables from body text - support both positional and named
              const positionalMatches = bodyText.match(/\{\{(\d+)\}\}/g)
              const namedMatches = bodyText.match(
                /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g
              )

              // Handle positional parameters ({{1}}, {{2}}, etc.)
              if (positionalMatches) {
                positionalMatches.forEach((match, index) => {
                  variables.push({
                    key: match,
                    example: component.example?.body_text?.[0]?.[index] || '',
                    type: 'positional',
                  })
                })
              }

              // Handle named parameters ({{name}}, {{order_id}}, etc.)
              if (namedMatches && component.example?.body_text_named_params) {
                namedMatches.forEach(match => {
                  const paramName = match.replace(/[{}]/g, '')
                  const exampleData =
                    component.example.body_text_named_params.find(
                      (p: any) => p.param_name === paramName
                    )
                  variables.push({
                    key: match,
                    example: exampleData?.example || '',
                    type: 'named',
                    name: paramName,
                  })
                })
              }
              break
            case 'FOOTER':
              footerText = component.text || ''
              break
            case 'BUTTONS':
              if (component.buttons && Array.isArray(component.buttons)) {
                component.buttons.forEach((btn: any) => {
                  const buttonType = btn.type?.toLowerCase() as any
                  const buttonObj: any = {
                    type: buttonType,
                    title: btn.text || btn.url || '',
                    payload: btn.payload,
                    url: btn.url,
                    phone: btn.phone_number,
                  }
                  // Handle OTP buttons (authentication templates)
                  if (buttonType === 'otp') {
                    buttonObj.otp_type = btn.otp_type
                  }
                  // Handle COPY_CODE buttons (utility/marketing templates)
                  if (buttonType === 'copy_code') {
                    buttonObj.example = btn.example || ''
                  }
                  buttons.push(buttonObj)
                })
              }
              break
          }
        })
      }

      setTemplateForm({
        name: template.name,
        language: template.language,
        description: template.description ?? '',
        externalId: template.externalId ?? '',
        body: template.body ?? '',
        componentsJson: template.components
          ? JSON.stringify(template.components, null, 2)
          : '',
        category:
          (template.category as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION') ??
          'UTILITY',
        isActive: template.isActive,
        submitToFacebook: false,
        // Enhanced fields
        headerType,
        headerText,
        headerMediaUrl,
        bodyText,
        footerText,
        buttons,
        variables,
      })
      setHasCustomComponentsJson(false)
      setEditingTemplateId(template.id)
    } else {
      setTemplateForm(defaultTemplateForm)
      setHasCustomComponentsJson(false)
      setEditingTemplateId(null)
    }
    setShowTemplateForm(true)
  }

  const handleTemplateFormChange = (
    field: keyof TemplateFormState,
    value: string | boolean
  ) => {
    // Auto-update externalId when name changes (only in create mode)
    if (field === 'name' && !editingTemplateId) {
      const sanitizedName = String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')

      setTemplateForm(prev => ({
        ...prev,
        name: value as string,
        externalId: sanitizedName || prev.externalId,
      }))
      return
    }

    if (field === 'componentsJson') {
      const stringValue =
        typeof value === 'string' ? value : String(value ?? '')
      setHasCustomComponentsJson(stringValue.trim().length > 0)
    }

    setTemplateForm(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleTemplateSubmit = async (overrideSubmitToFacebook?: boolean) => {
    // Check for duplicate name in create mode
    if (!editingTemplateId) {
      const isDuplicate = templates.some(
        template =>
          template.name.toLowerCase() === templateForm.name.trim().toLowerCase()
      )

      if (isDuplicate) {
        showWarning(
          'Duplicate Name',
          'A template with this name already exists. Please use a different name.'
        )
        return
      }
    }

    if (!templateForm.name.trim()) {
      showWarning('Missing Name', 'Template name is required.')
      return
    }

    // Build components array from the enhanced form
    const builtComponents: any[] = []

    // Add header component if specified
    if (templateForm.headerType) {
      const headerComponent: any = {
        type: 'HEADER',
        format: templateForm.headerType.toUpperCase(),
      }

      if (templateForm.headerType === 'text' && templateForm.headerText) {
        headerComponent.text = templateForm.headerText
      } else if (templateForm.headerType !== 'text') {
        // For IMAGE, VIDEO, DOCUMENT headers, Facebook requires an example even if empty
        // When submitting to Facebook, validate that media URL is provided
        const needsExample = ['image', 'video', 'document'].includes(
          templateForm.headerType
        )
        const isSubmittingToFacebook =
          overrideSubmitToFacebook !== undefined
            ? overrideSubmitToFacebook
            : templateForm.submitToFacebook

        if (
          needsExample &&
          isSubmittingToFacebook &&
          !templateForm.headerMediaUrl
        ) {
          showWarning(
            'Missing Header Example',
            `Templates with ${templateForm.headerType.toUpperCase()} header type require a sample URL when submitting to Facebook.`
          )
          return
        }

        if (
          templateForm.headerMediaUrl ||
          (needsExample && isSubmittingToFacebook)
        ) {
          // Facebook expects header_handle for IMAGE/VIDEO/DOCUMENT template creation
          headerComponent.example = {
            header_handle: [
              templateForm.headerMediaUrl ||
                'https://example.com/sample-image.jpg',
            ],
          }
        }
      }

      builtComponents.push(headerComponent)
    }

    // Add body component
    if (templateForm.bodyText) {
      const bodyComponent: any = {
        type: 'BODY',
        text: templateForm.bodyText,
      }

      // Add example values for variables if provided
      if (templateForm.variables.length > 0) {
        // Separate positional and named parameters
        const positionalVars = templateForm.variables.filter(
          v => v.type === 'positional'
        )
        const namedVars = templateForm.variables.filter(v => v.type === 'named')

        // Add positional parameters
        if (positionalVars.length > 0) {
          const bodyExamples = positionalVars
            .map(v => v.example)
            .filter(Boolean)
          if (bodyExamples.length > 0) {
            bodyComponent.example = {
              body_text: [bodyExamples],
            }
          }
        }

        // Add named parameters
        if (namedVars.length > 0) {
          const namedParams = namedVars
            .filter(v => v.name && v.example)
            .map(v => ({
              param_name: v.name,
              example: v.example,
            }))
          if (namedParams.length > 0) {
            if (!bodyComponent.example) {
              bodyComponent.example = {}
            }
            bodyComponent.example.body_text_named_params = namedParams
          }
        }
      }

      builtComponents.push(bodyComponent)
    }

    // Add footer component if specified
    if (templateForm.footerText) {
      builtComponents.push({
        type: 'FOOTER',
        text: templateForm.footerText,
      })
    }

    // Add buttons component if specified
    if (templateForm.buttons.length > 0) {
      const buttonComponents: any[] = []
      templateForm.buttons.forEach(btn => {
        const buttonComponent: any = {
          type: btn.type.toUpperCase(),
        }

        switch (btn.type) {
          case 'quick_reply':
            buttonComponent.text = btn.title
            // Note: payload is not sent to Facebook template API, only used when sending messages
            break
          case 'url':
            buttonComponent.text = btn.title
            buttonComponent.url = btn.url
            break
          case 'phone_number':
            buttonComponent.text = btn.title
            buttonComponent.phone_number = btn.phone
            break
          case 'copy_code':
            buttonComponent.example = btn.example
            break
          case 'otp':
            // OTP buttons for authentication templates
            if (btn.title) buttonComponent.text = btn.title
            if (btn.otp_type) buttonComponent.otp_type = btn.otp_type
            break
          case 'flow':
            buttonComponent.text = btn.text
            buttonComponent.flow_id = btn.flow_id
            // Add other flow properties as needed
            if (btn.flow_name) buttonComponent.flow_name = btn.flow_name
            if (btn.flow_json) buttonComponent.flow_json = btn.flow_json
            if (btn.flow_action) buttonComponent.flow_action = btn.flow_action
            if (btn.navigate_screen)
              buttonComponent.navigate_screen = btn.navigate_screen
            if (btn.icon) buttonComponent.icon = btn.icon
            break
        }

        buttonComponents.push(buttonComponent)
      })

      builtComponents.push({
        type: 'BUTTONS',
        buttons: buttonComponents,
      })
    }

    let componentsToUse = builtComponents
    if (hasCustomComponentsJson) {
      const customComponentsJson = templateForm.componentsJson.trim()
      if (customComponentsJson) {
        try {
          const parsedComponents = JSON.parse(customComponentsJson)
          if (!Array.isArray(parsedComponents)) {
            showError('Invalid Components', 'Components JSON must be an array.')
            return
          }

          const builtComparable = JSON.stringify(builtComponents)
          const parsedComparable = JSON.stringify(parsedComponents)

          if (
            parsedComparable !== builtComparable ||
            builtComponents.length === 0
          ) {
            componentsToUse = parsedComponents as any[]
          }
        } catch (error: any) {
          showError(
            'Invalid Components',
            error?.message ?? 'Components must be valid JSON.'
          )
          return
        }
      }
    }

    const payload: any = {
      channel: 'whatsapp',
      name: templateForm.name.trim(),
      language: templateForm.language.trim() || 'en_US',
      description: templateForm.description.trim() || undefined,
      externalId: templateForm.externalId.trim() || undefined,
      body: templateForm.body.trim() || undefined,
      components: componentsToUse,
      category: templateForm.category,
      isActive: templateForm.isActive,
      submitToFacebook:
        overrideSubmitToFacebook !== undefined
          ? overrideSubmitToFacebook
          : templateForm.submitToFacebook,
    }

    try {
      setSavingTemplate(true)
      if (editingTemplateId) {
        const response = await api.sessions.updateMessageTemplate(
          editingTemplateId,
          payload
        )
        if (!response.success) {
          throw new Error(response.error || 'Update failed')
        }

        // Handle Facebook submission status for updates too
        if ((response.data as any)?.facebookStatus === 'PENDING') {
          showSuccess(
            'Template Submitted',
            'Template updated and submitted to Facebook for approval. Use Sync button to update status.'
          )
        } else if ((response.data as any)?.facebookStatus === 'FAILED') {
          showWarning(
            'Partial Success',
            `Template updated in database but failed to submit to Facebook: ${(response.data as any)?.facebookError || 'Unknown error'}`
          )
        } else {
          showSuccess(
            'Template Updated',
            'Message template saved successfully.'
          )
        }
      } else {
        const response = await api.sessions.createMessageTemplate(payload)
        if (!response.success) {
          throw new Error(response.error || 'Create failed')
        }

        if ((response.data as any)?.facebookStatus === 'PENDING') {
          showSuccess(
            'Template Submitted',
            'Template created and submitted to Facebook for approval. Use Sync button to update status.'
          )
        } else if ((response.data as any)?.facebookStatus === 'FAILED') {
          showWarning(
            'Partial Success',
            `Template created in database but failed to submit to Facebook: ${(response.data as any)?.facebookError || 'Unknown error'}`
          )
        } else {
          showSuccess(
            'Template Created',
            'Message template created successfully.'
          )
        }
      }
      setShowTemplateForm(false)
      setTemplateForm(defaultTemplateForm)
      setHasCustomComponentsJson(false)
      setEditingTemplateId(null)
      await queryClient.invalidateQueries({
        queryKey: messageTemplatesRootKey,
      })
    } catch (error: any) {
      console.error('Failed to save template:', error)
      showError(
        'Save Failed',
        error?.message ?? 'Unable to save template. Please try again.'
      )
    } finally {
      setSavingTemplate(false)
    }
  }

  const handleDeleteTemplate = async (templateId: number) => {
    const template = templates.find(item => item.id === templateId)
    if (!template) return

    const confirmed = await confirm({
      title: 'Delete Template',
      description: `Delete template "${template.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
    })
    if (!confirmed) {
      return
    }

    try {
      await api.sessions.deleteMessageTemplate(templateId)
      showSuccess('Template Removed', 'Template deleted.')
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId('')
      }
      await queryClient.invalidateQueries({
        queryKey: messageTemplatesRootKey,
      })
    } catch (error: any) {
      console.error('Failed to delete template:', error)
      showError(
        'Delete Failed',
        error?.message ?? 'Unable to delete template. Please try again.'
      )
    }
  }

  // Add helper functions for managing template components
  const addVariable = (type: 'positional' | 'named' = 'positional') => {
    if (type === 'positional') {
      const newVariableIndex =
        templateForm.variables.filter(v => v.type === 'positional').length + 1
      const newVariableKey = `{{${newVariableIndex}}}`

      setTemplateForm(prev => ({
        ...prev,
        variables: [
          ...prev.variables,
          { key: newVariableKey, example: '', type: 'positional' },
        ],
        bodyText:
          prev.bodyText +
          (prev.bodyText.endsWith(' ') || prev.bodyText === ''
            ? newVariableKey
            : ` ${newVariableKey}`),
      }))
    } else {
      // Named variable - start with a placeholder name
      const newVariableName = 'variable_name'
      const newVariableKey = `{{${newVariableName}}}`

      setTemplateForm(prev => ({
        ...prev,
        variables: [
          ...prev.variables,
          {
            key: newVariableKey,
            example: '',
            type: 'named',
            name: newVariableName,
          },
        ],
        bodyText:
          prev.bodyText +
          (prev.bodyText.endsWith(' ') || prev.bodyText === ''
            ? newVariableKey
            : ` ${newVariableKey}`),
      }))
    }
  }

  const updateVariable = (
    index: number,
    field: 'key' | 'example' | 'name',
    value: string
  ) => {
    setTemplateForm(prev => {
      const newVariables = [...prev.variables]
      const oldKey = newVariables[index].key

      // Handle name updates for named variables
      if (field === 'name' && newVariables[index].type === 'named') {
        const sanitizedName = value.replace(/[^a-zA-Z0-9_]/g, '_')
        const newKey = `{{${sanitizedName}}}`
        newVariables[index] = {
          ...newVariables[index],
          name: sanitizedName,
          key: newKey,
        }

        // Update the body text to replace old key with new key
        const escapedOldKey = oldKey.replace(/[{}]/g, '\\$&')
        return {
          ...prev,
          variables: newVariables,
          bodyText: prev.bodyText.replace(
            new RegExp(escapedOldKey, 'g'),
            newKey
          ),
        }
      }

      // Handle regular field updates
      newVariables[index] = { ...newVariables[index], [field]: value }

      // If the key is being updated directly (for positional variables)
      if (field === 'key') {
        // Escape special regex characters in the old key
        const escapedOldKey = oldKey.replace(/[{}]/g, '\\\\\\$&')
        return {
          ...prev,
          variables: newVariables,
          bodyText: prev.bodyText.replace(
            new RegExp(escapedOldKey, 'g'),
            value
          ),
        }
      }

      return { ...prev, variables: newVariables }
    })
  }

  const removeVariable = (index: number) => {
    setTemplateForm(prev => {
      const variablesCopy = [...prev.variables]
      const removed = variablesCopy.splice(index, 1)[0]
      if (!removed) {
        return prev
      }

      // Remove from body text
      let nextBodyText = prev.bodyText.replace(
        new RegExp(escapeRegExp(removed.key), 'g'),
        ''
      )

      // Only reindex positional variables
      const positionalVars = variablesCopy.filter(v => v.type === 'positional')
      const namedVars = variablesCopy.filter(v => v.type === 'named')

      const reindexedPositional = positionalVars.map((variable, idx) => {
        const newKey = `{{${idx + 1}}}`
        const oldKey = variable.key

        // Update body text if key changed
        if (oldKey !== newKey) {
          nextBodyText = nextBodyText.replace(
            new RegExp(escapeRegExp(oldKey), 'g'),
            newKey
          )
        }

        return {
          ...variable,
          key: newKey,
        }
      })

      return {
        ...prev,
        variables: [...reindexedPositional, ...namedVars],
        bodyText: nextBodyText,
      }
    })
  }

  const addButton = () => {
    setTemplateForm(prev => ({
      ...prev,
      buttons: [...prev.buttons, { type: 'quick_reply', title: '' }],
    }))
  }

  const updateButton = (index: number, field: string, value: string) => {
    setTemplateForm(prev => {
      const newButtons = [...prev.buttons]
      const updatedButton = { ...newButtons[index] }

      // Handle special cases when updating fields
      if (field === 'type') {
        // When changing button type, ensure appropriate fields are set
        if (value === 'quick_reply') {
          // Remove fields not used by quick_reply
          delete updatedButton.url
          delete updatedButton.phone
          delete updatedButton.example
          delete updatedButton.text
          delete updatedButton.flow_id
          delete updatedButton.flow_name
          delete updatedButton.flow_json
          delete updatedButton.flow_action
          delete updatedButton.navigate_screen
          delete updatedButton.icon
        } else if (value === 'url') {
          // Remove fields not used by url
          delete updatedButton.phone
          delete updatedButton.example
          delete updatedButton.text
          delete updatedButton.flow_id
          delete updatedButton.flow_name
          delete updatedButton.flow_json
          delete updatedButton.flow_action
          delete updatedButton.navigate_screen
          delete updatedButton.icon
        } else if (value === 'phone_number') {
          // Remove fields not used by phone_number
          delete updatedButton.url
          delete updatedButton.example
          delete updatedButton.text
          delete updatedButton.flow_id
          delete updatedButton.flow_name
          delete updatedButton.flow_json
          delete updatedButton.flow_action
          delete updatedButton.navigate_screen
          delete updatedButton.icon
        } else if (value === 'copy_code') {
          // Set default example for copy code buttons if not already set
          if (!updatedButton.example) {
            updatedButton.example = ''
          }
          // Remove fields not used by copy_code
          delete updatedButton.url
          delete updatedButton.phone
          delete updatedButton.text
          delete updatedButton.flow_id
          delete updatedButton.flow_name
          delete updatedButton.flow_json
          delete updatedButton.flow_action
          delete updatedButton.navigate_screen
          delete updatedButton.icon
          delete updatedButton.otp_type
        } else if (value === 'otp') {
          // Set default otp_type for OTP buttons
          if (!updatedButton.otp_type) {
            updatedButton.otp_type = 'copy_code'
          }
          // Remove fields not used by otp
          delete updatedButton.url
          delete updatedButton.phone
          delete updatedButton.example
          delete updatedButton.text
          delete updatedButton.flow_id
          delete updatedButton.flow_name
          delete updatedButton.flow_json
          delete updatedButton.flow_action
          delete updatedButton.navigate_screen
          delete updatedButton.icon
        } else if (value === 'flow') {
          // Set default text for flow buttons if not already set
          if (!updatedButton.text) {
            updatedButton.text = ''
          }
          if (!updatedButton.flow_id) {
            updatedButton.flow_id = ''
          }
          // Remove fields not used by flow
          delete updatedButton.url
          delete updatedButton.phone
          delete updatedButton.example
        }
      }

      // Update the field
      ;(updatedButton as any)[field] = value

      return {
        ...prev,
        buttons: newButtons.map((btn, i) =>
          i === index ? updatedButton : btn
        ),
      }
    })
  }

  const removeButton = (index: number) => {
    setTemplateForm(prev => {
      const newButtons = [...prev.buttons]
      newButtons.splice(index, 1)
      return { ...prev, buttons: newButtons }
    })
  }

  return (
    <>
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Outbound Messaging
            </h3>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Recipient
              </label>
              <div className="flex items-start gap-2">
                <div className="relative flex-1">
                  <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={recipient}
                    onChange={event => setRecipient(event.target.value)}
                    onFocus={() => setShowContactSuggestions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowContactSuggestions(false), 150)
                    }
                    placeholder="Enter contact ID or phone number"
                    className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-36 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter phone number, social media ID, or select from
                    contacts.
                  </p>

                  {selectedContact && recipient.trim() && (
                    <span className="pointer-events-none absolute right-3 top-3 max-w-[50%] truncate text-xs text-gray-500">
                      {selectedContact.firstName ?? ''}{' '}
                      {selectedContact.lastName ?? ''}
                    </span>
                  )}

                  {/* Contact suggestions dropdown */}
                  {showContactSuggestions && recipient.trim().length >= 2 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      {searchingContacts && (
                        <div className="px-3 py-2 text-xs text-gray-500">
                          Searching...
                        </div>
                      )}
                      {contactSearchData?.contacts?.length ? (
                        <ul className="max-h-56 overflow-auto py-1">
                          {contactSearchData.contacts.map(contact => (
                            <li key={contact.id}>
                              <button
                                type="button"
                                onMouseDown={() => {
                                  if (contact.phoneNumber)
                                    setRecipient(String(contact.phoneNumber))
                                  setSelectedContact(contact)
                                  setShowContactSuggestions(false)
                                }}
                                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-blue-50"
                              >
                                <span className="text-gray-800">
                                  {contact.firstName ?? ''}{' '}
                                  {contact.lastName ?? ''}
                                </span>
                                <span className="ml-2 font-mono text-gray-600">
                                  {contact.phoneNumber ?? ''}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        !searchingContacts && (
                          <div className="px-3 py-2 text-xs text-gray-500">
                            No matches
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    // Clear all contact filters when opening the phonebook
                    setContactFilters({
                      hasPhone: false,
                      hasMessenger: false,
                      hasTelegram: false,
                      hasInstagram: false,
                      hasWeChat: false,
                      hasLine: false,
                    })
                    setContactSearchTerm('')
                    setIsContactDialogOpen(true)
                    setShowContactSuggestions(false)
                  }}
                  aria-label="Open address book"
                >
                  <BookUser className="h-4 w-4" />
                </Button>
              </div>

              {/* Channel selector for manual entry (no contact selected) */}
              {!selectedContact && recipient.trim().length > 0 && (
                <div className="mt-3 rounded-md border border-amber-100 bg-amber-50 p-3">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Select Channel
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CONTACT_FILTERS.map(filter => {
                      const Icon = filter.icon
                      const channelType = mapChannelLabelToType(filter.label)
                      const isSelected = channel === channelType

                      return (
                        <button
                          key={filter.key}
                          type="button"
                          onClick={() => {
                            console.log(
                              `📡 Channel selected: ${filter.label} (${channelType})`
                            )
                            setChannel(channelType)
                          }}
                          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-all duration-200 ${
                            isSelected
                              ? 'border-blue-500 bg-blue-500 text-white shadow-md scale-105'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50 hover:scale-105'
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          <span>{filter.label}</span>
                          {isSelected && (
                            <span className="ml-1 text-xs">✓</span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                  <p className="mt-2 text-xs text-gray-600">
                    Selected:{' '}
                    <span className="font-semibold">
                      {CONTACT_FILTERS.find(
                        f => mapChannelLabelToType(f.label) === channel
                      )?.label || 'WhatsApp'}
                    </span>
                  </p>
                </div>
              )}

              {/* Social Media Channel Selection */}
              {selectedContact && (
                <div className="mt-3 rounded-md border border-blue-100 bg-blue-50 p-3">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Send via (select channels)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CONTACT_FILTERS.map(filter => {
                      const hasChannel = contactHasField(
                        selectedContact,
                        filter.field
                      )
                      const Icon = filter.icon
                      const isSelected = selectedChannels[filter.field]
                      const contactValue = selectedContact[filter.field]

                      return (
                        <label
                          key={filter.key}
                          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                            !hasChannel
                              ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                              : isSelected
                                ? 'border-blue-500 bg-blue-500 text-white cursor-pointer'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!hasChannel}
                            onChange={e => {
                              if (hasChannel) {
                                setSelectedChannels(prev => ({
                                  ...prev,
                                  [filter.field]: e.target.checked,
                                }))
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <Icon className="h-3.5 w-3.5" />
                          <span>{filter.label}</span>
                          {hasChannel && contactValue && (
                            <span className="font-mono text-xs opacity-75">
                              ({String(contactValue)})
                            </span>
                          )}
                          {!hasChannel && (
                            <span className="text-xs opacity-50">(N/A)</span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                  {Object.values(selectedChannels).every(v => !v) && (
                    <p className="mt-2 text-xs text-orange-600">
                      ⚠️ Please select at least one channel to send the message
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Message Template
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedTemplateId}
                    onChange={event => {
                      const value = event.target.value
                      setSelectedTemplateId(value ? Number(value) : '')
                    }}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select template</option>
                    {templates.map(template => (
                      <option
                        key={template.id}
                        value={template.id}
                        disabled={!template.isActive}
                      >
                        {template.name}
                        {template.isActive ? '' : ' (inactive)'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() =>
                      queryClient.invalidateQueries({
                        queryKey: messageTemplatesKey,
                      })
                    }
                    className="inline-flex items-center rounded-md border border-gray-300 px-2 py-2 text-sm text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Refresh templates"
                  >
                    {loadingTemplates ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </button>
                  {isSupervisor && (
                    <>
                      <button
                        type="button"
                        onClick={handleFetchWhatsAppTemplates}
                        disabled={fetchingTemplates}
                        className="inline-flex items-center rounded-md border border-green-500 px-2 py-2 text-sm text-green-600 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                        title="Sync templates"
                      >
                        {fetchingTemplates ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => openTemplateForm()}
                        className="inline-flex items-center rounded-md border border-blue-500 px-2 py-2 text-sm text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Create new template"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
                {selectedTemplate && selectedTemplate.description && (
                  <p className="text-xs text-gray-500">
                    {selectedTemplate.description}
                  </p>
                )}

                {/* Template Parameters Input */}
                {selectedTemplate && templatePlaceholderCount > 0 && (
                  <div className="mt-3 space-y-2 rounded-md border border-blue-100 bg-blue-50 p-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Template Parameters
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Fill in values for placeholders in the template
                    </p>
                    {templateParameters.map((param, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-600 w-16">
                          {`{{${index + 1}}}`}
                        </span>
                        <input
                          type="text"
                          value={param}
                          onChange={e => {
                            const newParams = [...templateParameters]
                            newParams[index] = e.target.value
                            setTemplateParameters(newParams)
                          }}
                          placeholder={`Value for {{${index + 1}}}`}
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Template Preview */}
                {selectedTemplate && (
                  <div className="mt-3 rounded-md border border-gray-200 bg-white shadow-sm">
                    <label className="block text-xs font-semibold text-gray-700 bg-gray-50 px-3 py-2 border-b border-gray-200">
                      Preview
                    </label>
                    <div className="p-3 space-y-3">
                      {(() => {
                        // Parse components from template
                        const components = Array.isArray(
                          selectedTemplate.components
                        )
                          ? selectedTemplate.components
                          : []

                        // Find header component
                        const headerComponent = components.find(
                          (c: any) => c.type?.toLowerCase() === 'header'
                        )

                        // Find body component
                        const bodyComponent = components.find(
                          (c: any) => c.type?.toLowerCase() === 'body'
                        )

                        // Find footer component
                        const footerComponent = components.find(
                          (c: any) => c.type?.toLowerCase() === 'footer'
                        )

                        // Find buttons component
                        const buttonsComponent = components.find(
                          (c: any) => c.type?.toLowerCase() === 'buttons'
                        )

                        return (
                          <>
                            {/* Header Preview */}
                            {headerComponent && (
                              <div className="border-b border-gray-100 pb-3">
                                {headerComponent.format === 'TEXT' &&
                                  headerComponent.text && (
                                    <div className="text-sm font-semibold text-gray-900">
                                      {headerComponent.text}
                                    </div>
                                  )}
                                {headerComponent.format === 'IMAGE' && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <span className="inline-block w-4 h-4 bg-gray-200 rounded"></span>
                                    <span>Image Header</span>
                                  </div>
                                )}
                                {headerComponent.format === 'VIDEO' && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <span className="inline-block w-4 h-4 bg-gray-200 rounded"></span>
                                    <span>Video Header</span>
                                  </div>
                                )}
                                {headerComponent.format === 'DOCUMENT' && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <span className="inline-block w-4 h-4 bg-gray-200 rounded"></span>
                                    <span>Document Header</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Body Preview */}
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                              {(() => {
                                let previewText =
                                  bodyComponent?.text ||
                                  selectedTemplate.body ||
                                  'No content available'

                                // Replace placeholders with parameter values
                                templateParameters.forEach((param, index) => {
                                  const placeholder = `{{${index + 1}}}`
                                  if (param.trim()) {
                                    previewText = previewText.replace(
                                      new RegExp(
                                        placeholder.replace(/[{}]/g, '\\$&'),
                                        'g'
                                      ),
                                      param
                                    )
                                  }
                                })

                                return renderWhatsAppFormatting(previewText)
                              })()}
                            </div>

                            {/* Footer Preview */}
                            {footerComponent && footerComponent.text && (
                              <div className="border-t border-gray-100 pt-3">
                                <div className="text-xs text-gray-500">
                                  {footerComponent.text}
                                </div>
                              </div>
                            )}

                            {/* Buttons Preview */}
                            {buttonsComponent &&
                              Array.isArray(buttonsComponent.buttons) &&
                              buttonsComponent.buttons.length > 0 && (
                                <div className="border-t border-gray-100 pt-3 space-y-2">
                                  {buttonsComponent.buttons.map(
                                    (button: any, index: number) => (
                                      <div
                                        key={index}
                                        className="flex items-center justify-center rounded-md border border-blue-500 bg-white px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
                                      >
                                        {button.type === 'QUICK_REPLY' && (
                                          <span>↩️ {button.text}</span>
                                        )}
                                        {button.type === 'URL' && (
                                          <span>🔗 {button.text}</span>
                                        )}
                                        {button.type === 'PHONE_NUMBER' && (
                                          <span>📞 {button.text}</span>
                                        )}
                                        {button.type === 'COPY_CODE' && (
                                          <span>
                                            📋 {button.text || 'Copy Code'}
                                          </span>
                                        )}
                                        {button.type === 'OTP' && (
                                          <span>
                                            🔐 {button.text || 'Copy Code'}
                                          </span>
                                        )}
                                        {button.type === 'FLOW' && (
                                          <span>
                                            🔄 {button.text || 'Open Flow'}
                                          </span>
                                        )}
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={assignToMe}
                  onChange={e => {
                    setAssignToMe(e.target.checked)
                    if (!e.target.checked) {
                      setSelectedTeamId(null)
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="font-medium">Assign to me</span>
                <span className="text-xs text-gray-500">
                  (Take over session directly)
                </span>
              </label>

              {assignToMe && agentTeams.length > 1 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 font-medium">
                    <span className="text-red-500">*</span> Team:
                  </label>
                  <select
                    value={selectedTeamId || ''}
                    onChange={e =>
                      setSelectedTeamId(
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select team...</option>
                    {agentTeams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={isSending}
              className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Message
            </button>
          </div>

          {isSupervisor && (
            <div className="border border-gray-200 rounded-md p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-800">
                  Message Templates
                </h4>
                <button
                  type="button"
                  onClick={() => openTemplateForm()}
                  className="inline-flex items-center rounded-md border border-blue-500 px-3 py-1 text-xs text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Plus className="mr-1 h-3 w-3" />
                  New Template
                </button>
              </div>
              {templates.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No templates available yet. Create one to start sending all
                  social media messages.
                </p>
              ) : (
                <ul className="space-y-2">
                  {templates.map(template => (
                    <li
                      key={template.id}
                      className="flex items-start justify-between rounded-md border border-gray-200 p-3"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {template.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Language: {template.language.toUpperCase()}
                          {template.externalId
                            ? ` • ${template.externalId}`
                            : ''}
                        </p>
                        {template.facebookError && (
                          <div className="mt-2 rounded-md bg-red-50 border border-red-200 px-2 py-1">
                            <p className="text-xs font-medium text-red-800">
                              Facebook Error:
                            </p>
                            <p className="text-xs text-red-700 mt-0.5">
                              {template.facebookError}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${template.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                        >
                          {template.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          type="button"
                          onClick={() => openTemplateForm(template)}
                          className="inline-flex items-center rounded-md border border-gray-300 p-1 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="inline-flex items-center rounded-md border border-red-400 p-1 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {showTemplateForm && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-4">
                  <h5 className="text-sm font-semibold text-gray-900">
                    {editingTemplateId ? 'Edit Template' : 'Create Template'}
                  </h5>

                  {/* Basic Template Information */}
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-blue-900">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={templateForm.name}
                        onChange={event =>
                          handleTemplateFormChange('name', event.target.value)
                        }
                        disabled={!!editingTemplateId} // Disable name field in edit mode
                        className={`w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${editingTemplateId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        placeholder="Template display name"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-blue-900">
                        Language code *
                      </label>
                      <select
                        value={templateForm.language}
                        onChange={event =>
                          handleTemplateFormChange(
                            'language',
                            event.target.value
                          )
                        }
                        className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="en_US">English (US)</option>
                        <option value="ms_MY">Malay</option>
                        <option value="zh_CN">Chinese (Simplified)</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-blue-900">
                        External ID (Facebook template name) *
                      </label>
                      <input
                        type="text"
                        value={templateForm.externalId}
                        onChange={event =>
                          handleTemplateFormChange(
                            'externalId',
                            event.target.value
                          )
                        }
                        disabled={true} // Always disable externalId - auto-populated from name
                        className="w-full rounded-md border border-blue-200 bg-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-not-allowed"
                        placeholder="Auto-generated from name"
                      />
                      <p className="text-xs text-gray-500">
                        Auto-generated from template name
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-blue-900">
                        Description
                      </label>
                      <input
                        type="text"
                        value={templateForm.description}
                        onChange={event =>
                          handleTemplateFormChange(
                            'description',
                            event.target.value
                          )
                        }
                        className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Template description"
                      />
                    </div>
                  </div>

                  {/* Template Category */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-blue-900">
                      Category (for Facebook submission)
                    </label>
                    <select
                      value={templateForm.category}
                      onChange={event =>
                        handleTemplateFormChange('category', event.target.value)
                      }
                      className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="UTILITY">Utility</option>
                      <option value="MARKETING">Marketing</option>
                      <option value="AUTHENTICATION">Authentication</option>
                    </select>
                  </div>

                  {/* Header Component */}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <h6 className="text-xs font-semibold text-gray-900">
                      Header
                    </h6>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-blue-900">
                          Header Type
                        </label>
                        <select
                          value={templateForm.headerType || ''}
                          onChange={event =>
                            handleTemplateFormChange(
                              'headerType',
                              event.target.value || ''
                            )
                          }
                          className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">None</option>
                          <option value="text">Text</option>
                          <option value="image">Image</option>
                          <option value="video">Video</option>
                          <option value="document">Document</option>
                          <option value="location">Location</option>
                        </select>
                      </div>

                      {templateForm.headerType === 'text' ? (
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-blue-900">
                            Header Text
                          </label>
                          <input
                            type="text"
                            value={templateForm.headerText}
                            onChange={event =>
                              handleTemplateFormChange(
                                'headerText',
                                event.target.value
                              )
                            }
                            className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Header text content"
                          />
                        </div>
                      ) : templateForm.headerType ? (
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-blue-900">
                            Sample Media URL{' '}
                            <span className="text-red-600">*</span>
                          </label>
                          <input
                            type="text"
                            value={templateForm.headerMediaUrl}
                            onChange={event =>
                              handleTemplateFormChange(
                                'headerMediaUrl',
                                event.target.value
                              )
                            }
                            className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="https://example.com/sample-image.jpg"
                            required
                          />
                          <p className="text-xs text-red-600 font-medium">
                            Required when submitting to Facebook: Provide a
                            sample/example URL for{' '}
                            {templateForm.headerType.toUpperCase()} header
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Body Component */}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <h6 className="text-xs font-semibold text-gray-900">
                      Body
                    </h6>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-blue-900">
                          Body Text *
                        </label>
                        <textarea
                          rows={3}
                          value={templateForm.bodyText}
                          onChange={event => {
                            const newBodyText = event.target.value

                            // Auto-detect and update variables from body text
                            const variableMatches =
                              newBodyText.match(/\{\{(\d+)\}\}/g) || []
                            const detectedVariables = variableMatches.map(
                              match => {
                                // Check if variable already exists in the form to preserve example values
                                const existingVar = templateForm.variables.find(
                                  v => v.key === match
                                )
                                return {
                                  key: match,
                                  example: existingVar?.example || '',
                                }
                              }
                            )

                            // Remove duplicates by key
                            const uniqueVariables = detectedVariables.filter(
                              (v, i, arr) =>
                                arr.findIndex(x => x.key === v.key) === i
                            )

                            // Update both bodyText and variables together
                            setTemplateForm(prev => ({
                              ...prev,
                              bodyText: newBodyText,
                              variables: uniqueVariables,
                            }))
                          }}
                          className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Template body content with {{1}}, {{2}} for variables"
                          maxLength={1024}
                        />
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.querySelector(
                                  'textarea[placeholder*="Template body content"]'
                                ) as HTMLTextAreaElement
                                if (!textarea) return
                                const start = textarea.selectionStart
                                const end = textarea.selectionEnd
                                const selectedText =
                                  templateForm.bodyText.substring(start, end)
                                const before = templateForm.bodyText.substring(
                                  0,
                                  start
                                )
                                const after =
                                  templateForm.bodyText.substring(end)
                                const newText =
                                  before + `*${selectedText || 'text'}*` + after
                                setTemplateForm(prev => ({
                                  ...prev,
                                  bodyText: newText,
                                }))
                                setTimeout(() => {
                                  textarea.focus()
                                  const newPos = selectedText
                                    ? start + 1
                                    : start + 6
                                  textarea.setSelectionRange(
                                    newPos,
                                    selectedText ? end + 1 : newPos
                                  )
                                }, 0)
                              }}
                              className="rounded border border-gray-300 px-2 py-1 text-xs font-bold !text-black hover:border-blue-500 hover:bg-blue-50 hover:!text-black"
                              title="Bold (*text*)"
                            >
                              B
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.querySelector(
                                  'textarea[placeholder*="Template body content"]'
                                ) as HTMLTextAreaElement
                                if (!textarea) return
                                const start = textarea.selectionStart
                                const end = textarea.selectionEnd
                                const selectedText =
                                  templateForm.bodyText.substring(start, end)
                                const before = templateForm.bodyText.substring(
                                  0,
                                  start
                                )
                                const after =
                                  templateForm.bodyText.substring(end)
                                const newText =
                                  before + `_${selectedText || 'text'}_` + after
                                setTemplateForm(prev => ({
                                  ...prev,
                                  bodyText: newText,
                                }))
                                setTimeout(() => {
                                  textarea.focus()
                                  const newPos = selectedText
                                    ? start + 1
                                    : start + 6
                                  textarea.setSelectionRange(
                                    newPos,
                                    selectedText ? end + 1 : newPos
                                  )
                                }, 0)
                              }}
                              className="rounded border border-gray-300 px-2 py-1 text-xs italic !text-black hover:border-blue-500 hover:bg-blue-50 hover:!text-black"
                              title="Italic (_text_)"
                            >
                              I
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.querySelector(
                                  'textarea[placeholder*="Template body content"]'
                                ) as HTMLTextAreaElement
                                if (!textarea) return
                                const start = textarea.selectionStart
                                const end = textarea.selectionEnd
                                const selectedText =
                                  templateForm.bodyText.substring(start, end)
                                const before = templateForm.bodyText.substring(
                                  0,
                                  start
                                )
                                const after =
                                  templateForm.bodyText.substring(end)
                                const newText =
                                  before + `~${selectedText || 'text'}~` + after
                                setTemplateForm(prev => ({
                                  ...prev,
                                  bodyText: newText,
                                }))
                                setTimeout(() => {
                                  textarea.focus()
                                  const newPos = selectedText
                                    ? start + 1
                                    : start + 6
                                  textarea.setSelectionRange(
                                    newPos,
                                    selectedText ? end + 1 : newPos
                                  )
                                }, 0)
                              }}
                              className="rounded border border-gray-300 px-2 py-1 text-xs line-through !text-black hover:border-blue-500 hover:bg-blue-50 hover:!text-black"
                              title="Strikethrough (~text~)"
                            >
                              S
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const textarea = document.querySelector(
                                  'textarea[placeholder*="Template body content"]'
                                ) as HTMLTextAreaElement
                                if (!textarea) return
                                const start = textarea.selectionStart
                                const end = textarea.selectionEnd
                                const selectedText =
                                  templateForm.bodyText.substring(start, end)
                                const before = templateForm.bodyText.substring(
                                  0,
                                  start
                                )
                                const after =
                                  templateForm.bodyText.substring(end)
                                const newText =
                                  before +
                                  `\`${selectedText || 'code'}\`` +
                                  after
                                setTemplateForm(prev => ({
                                  ...prev,
                                  bodyText: newText,
                                }))
                                setTimeout(() => {
                                  textarea.focus()
                                  const newPos = selectedText
                                    ? start + 1
                                    : start + 6
                                  textarea.setSelectionRange(
                                    newPos,
                                    selectedText ? end + 1 : newPos
                                  )
                                }, 0)
                              }}
                              className="rounded border border-gray-300 px-2 py-1 font-mono text-xs !text-black hover:border-blue-500 hover:bg-blue-50 hover:!text-black"
                              title="Monospace (`code`)"
                            >
                              {'<>'}
                            </button>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  const emojiPicker = document.getElementById(
                                    'emoji-picker-body'
                                  ) as HTMLDivElement
                                  if (emojiPicker) {
                                    emojiPicker.classList.toggle('hidden')
                                  }
                                }}
                                className="rounded border border-gray-300 px-2 py-1 text-xs !text-black hover:border-blue-500 hover:bg-blue-50 hover:!text-black"
                                title="Insert emoji"
                              >
                                😊
                              </button>
                              <div
                                id="emoji-picker-body"
                                className="absolute bottom-8 left-0 z-10 hidden w-64 rounded-md border border-gray-200 bg-white p-2 shadow-lg"
                              >
                                <div className="grid grid-cols-8 gap-1">
                                  {[
                                    '😊',
                                    '😂',
                                    '❤️',
                                    '👍',
                                    '🙏',
                                    '✅',
                                    '🎉',
                                    '🔥',
                                    '💯',
                                    '👏',
                                    '🌟',
                                    '💪',
                                    '🎯',
                                    '📌',
                                    '✨',
                                    '💼',
                                    '📱',
                                    '⚡',
                                    '🚀',
                                    '💡',
                                    '📧',
                                    '📞',
                                    '🏆',
                                    '🎁',
                                    '⏰',
                                    '📅',
                                    '✔️',
                                    '❌',
                                    '⭐',
                                    '🌈',
                                    '☀️',
                                    '🌙',
                                  ].map(emoji => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      onClick={() => {
                                        const textarea = document.querySelector(
                                          'textarea[placeholder*="Template body content"]'
                                        ) as HTMLTextAreaElement
                                        if (!textarea) return
                                        const start = textarea.selectionStart
                                        const before =
                                          templateForm.bodyText.substring(
                                            0,
                                            start
                                          )
                                        const after =
                                          templateForm.bodyText.substring(start)
                                        const newText = before + emoji + after
                                        setTemplateForm(prev => ({
                                          ...prev,
                                          bodyText: newText,
                                        }))
                                        setTimeout(() => {
                                          textarea.focus()
                                          textarea.setSelectionRange(
                                            start + emoji.length,
                                            start + emoji.length
                                          )
                                          const emojiPicker =
                                            document.getElementById(
                                              'emoji-picker-body'
                                            )
                                          if (emojiPicker)
                                            emojiPicker.classList.add('hidden')
                                        }, 0)
                                      }}
                                      className="hover:bg-blue-50 rounded p-1 text-lg transition-colors"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">
                            {templateForm.bodyText.length}/1024
                          </span>
                        </div>
                        <p className="text-xs text-gray-400">
                          Use *bold*, _italic_, ~strikethrough~, or `monospace`
                          formatting
                        </p>
                      </div>

                      {/* Variables Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-xs font-medium text-blue-900">
                            Variables
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => addVariable('positional')}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                              <Plus className="h-3 w-3" />
                              Positional ({'{'}
                              {'{'}1{'}'}
                              {'}'})
                            </button>
                            <button
                              type="button"
                              onClick={() => addVariable('named')}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                            >
                              <Plus className="h-3 w-3" />
                              Named ({'{'}
                              {'{'}name{'}'}
                              {'}'})
                            </button>
                          </div>
                        </div>

                        {templateForm.variables.length > 0 ? (
                          <div className="space-y-3">
                            {templateForm.variables.map((variable, index) => (
                              <div
                                key={index}
                                className={`flex items-end gap-2 p-3 rounded-lg border-2 ${
                                  variable.type === 'positional'
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-green-50 border-green-200'
                                }`}
                              >
                                {variable.type === 'positional' ? (
                                  <div className="flex-1">
                                    <label className="text-xs font-semibold text-blue-900 flex items-center gap-1">
                                      <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-600 text-white rounded text-xs font-bold">
                                        P
                                      </span>
                                      Positional Variable: {variable.key}
                                    </label>
                                    <input
                                      type="text"
                                      value={variable.key}
                                      disabled
                                      className="mt-1 w-full rounded-md border border-blue-300 bg-blue-100 px-3 py-2 text-sm text-gray-700 cursor-not-allowed font-mono"
                                      placeholder="{{1}}"
                                    />
                                  </div>
                                ) : (
                                  <div className="flex-1">
                                    <label className="text-xs font-semibold text-green-900 flex items-center gap-1 mb-1">
                                      <span className="inline-flex items-center justify-center w-5 h-5 bg-green-600 text-white rounded text-xs font-bold">
                                        N
                                      </span>
                                      Named Variable
                                    </label>
                                    <input
                                      type="text"
                                      value={variable.name || ''}
                                      onChange={event =>
                                        updateVariable(
                                          index,
                                          'name',
                                          event.target.value
                                        )
                                      }
                                      className="w-full rounded-md border border-green-300 bg-white px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 font-mono"
                                      placeholder="variable_name"
                                    />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <label className="text-xs font-semibold text-gray-700">
                                    Example Value
                                  </label>
                                  <input
                                    type="text"
                                    value={variable.example}
                                    onChange={event =>
                                      updateVariable(
                                        index,
                                        'example',
                                        event.target.value
                                      )
                                    }
                                    onBlur={event => {
                                      const trimmedValue =
                                        event.target.value.trim()
                                      if (trimmedValue !== event.target.value) {
                                        updateVariable(
                                          index,
                                          'example',
                                          trimmedValue
                                        )
                                      }
                                    }}
                                    className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder={`Example value`}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeVariable(index)}
                                  className="mb-2 inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-white hover:bg-red-600 rounded-md transition-colors border border-red-300 hover:border-red-600"
                                  title="Remove variable"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 px-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                            <p className="text-sm font-medium text-gray-600 mb-2">
                              No variables added yet
                            </p>
                            <p className="text-xs text-gray-500 mb-4">
                              Add variables to personalize your message
                            </p>
                            <div className="flex justify-center gap-3">
                              <div className="text-center">
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-mono mb-1">
                                  {'{'}
                                  {'{'}1{'}'}
                                  {'}'}
                                </div>
                                <p className="text-xs text-gray-600">
                                  Positional
                                </p>
                              </div>
                              <div className="text-center">
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-mono mb-1">
                                  {'{'}
                                  {'{'}name{'}'}
                                  {'}'}
                                </div>
                                <p className="text-xs text-gray-600">Named</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer Component */}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <h6 className="text-xs font-semibold text-gray-900">
                      Footer
                    </h6>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-blue-900">
                        Footer Text
                      </label>
                      <input
                        type="text"
                        value={templateForm.footerText}
                        onChange={event =>
                          handleTemplateFormChange(
                            'footerText',
                            event.target.value
                          )
                        }
                        className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Footer text content"
                      />
                    </div>
                  </div>

                  {/* Buttons Component */}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <div className="flex items-center justify-between mb-3">
                      <h6 className="text-xs font-semibold text-gray-900">
                        Buttons
                      </h6>
                      <button
                        type="button"
                        onClick={addButton}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add Button
                      </button>
                    </div>

                    {templateForm.buttons.length > 0 ? (
                      <div className="space-y-3">
                        {templateForm.buttons.map((button, index) => (
                          <div
                            key={index}
                            className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4 space-y-3"
                          >
                            <div className="flex items-center justify-between">
                              <h6 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded text-xs font-bold">
                                  {index + 1}
                                </span>
                                Button {index + 1}
                              </h6>
                              <button
                                type="button"
                                onClick={() => removeButton(index)}
                                className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-white hover:bg-red-600 rounded-md transition-colors border border-red-300 hover:border-red-600"
                                title="Remove button"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-blue-900">
                                  Button Type
                                </label>
                                <select
                                  value={button.type}
                                  onChange={event =>
                                    updateButton(
                                      index,
                                      'type',
                                      event.target.value
                                    )
                                  }
                                  className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="quick_reply">
                                    Quick Reply (Max 10)
                                  </option>
                                  <option value="url">URL (Max 2)</option>
                                  <option value="phone_number">
                                    Phone Number (Max 1)
                                  </option>
                                  <option value="copy_code">
                                    Copy Code (Max 1, Utility/Marketing)
                                  </option>
                                  <option value="otp">
                                    OTP - Copy Code (Max 1, Authentication)
                                  </option>
                                  <option value="flow">Flows</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                  Max 10 buttons total. Limits apply per type.
                                </p>
                              </div>

                              {/* Button Title - not used for copy_code or flow types, but used for otp */}
                              {button.type !== 'copy_code' &&
                                button.type !== 'flow' && (
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-blue-900">
                                      Button Title
                                    </label>
                                    <input
                                      type="text"
                                      value={button.title}
                                      onChange={event =>
                                        updateButton(
                                          index,
                                          'title',
                                          event.target.value
                                        )
                                      }
                                      className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Button text"
                                    />
                                  </div>
                                )}
                            </div>

                            {button.type === 'url' && (
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-blue-900">
                                  URL
                                </label>
                                <input
                                  type="text"
                                  value={button.url || ''}
                                  onChange={event =>
                                    updateButton(
                                      index,
                                      'url',
                                      event.target.value
                                    )
                                  }
                                  className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="https://example.com"
                                />
                              </div>
                            )}

                            {button.type === 'phone_number' && (
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-blue-900">
                                  Phone Number
                                </label>
                                <input
                                  type="text"
                                  value={button.phone || ''}
                                  onChange={event =>
                                    updateButton(
                                      index,
                                      'phone',
                                      event.target.value
                                    )
                                  }
                                  className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="+1234567890"
                                />
                              </div>
                            )}

                            {button.type === 'copy_code' && (
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-blue-900">
                                  Code to Copy
                                </label>
                                <input
                                  type="text"
                                  value={button.example || ''}
                                  onChange={event =>
                                    updateButton(
                                      index,
                                      'example',
                                      event.target.value
                                    )
                                  }
                                  className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Code to copy to clipboard"
                                  maxLength={15}
                                />
                                <p className="text-xs text-blue-700">
                                  Maximum 15 characters
                                </p>
                              </div>
                            )}

                            {button.type === 'otp' && (
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-blue-900">
                                  OTP Type
                                </label>
                                <select
                                  value={button.otp_type || 'copy_code'}
                                  onChange={event =>
                                    updateButton(
                                      index,
                                      'otp_type',
                                      event.target.value
                                    )
                                  }
                                  className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="copy_code">Copy Code</option>
                                </select>
                                <p className="text-xs text-blue-700">
                                  For authentication templates (preset text +
                                  copy code button)
                                </p>
                              </div>
                            )}

                            {button.type === 'flow' && (
                              <div className="space-y-2">
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-blue-900">
                                    Flow Text
                                  </label>
                                  <input
                                    type="text"
                                    value={button.text || ''}
                                    onChange={event =>
                                      updateButton(
                                        index,
                                        'text',
                                        event.target.value
                                      )
                                    }
                                    className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Button text"
                                    maxLength={25}
                                  />
                                  <p className="text-xs text-blue-700">
                                    Maximum 25 characters
                                  </p>
                                </div>
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-blue-900">
                                    Flow ID
                                  </label>
                                  <input
                                    type="text"
                                    value={button.flow_id || ''}
                                    onChange={event =>
                                      updateButton(
                                        index,
                                        'flow_id',
                                        event.target.value
                                      )
                                    }
                                    className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Flow identifier"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 px-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                        <p className="text-sm font-medium text-gray-600 mb-2">
                          No buttons added yet
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                          Add interactive buttons to enhance user engagement
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 text-xs">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                            Quick Reply
                          </span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                            URL
                          </span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                            Phone
                          </span>
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                            Copy Code
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Template Preview */}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <h6 className="text-xs font-semibold text-gray-900">
                      Template Preview
                    </h6>
                    <div className="rounded-md border border-blue-200 bg-white p-3">
                      <div className="space-y-2">
                        {/* Header Preview */}
                        {templateForm.headerType && (
                          <div className="font-medium text-gray-900">
                            {templateForm.headerType === 'text'
                              ? templateForm.headerText
                              : `[${templateForm.headerType.toUpperCase()} MEDIA]`}
                          </div>
                        )}

                        {/* Body Preview */}
                        <div className="text-gray-700 whitespace-pre-wrap">
                          {(() => {
                            let previewText = templateForm.bodyText

                            // Replace variables with their example values
                            templateForm.variables.forEach(variable => {
                              if (variable.example) {
                                previewText = previewText.replace(
                                  new RegExp(
                                    variable.key.replace(/[{}]/g, '\\$&'),
                                    'g'
                                  ),
                                  variable.example
                                )
                              }
                            })

                            return renderWhatsAppFormatting(previewText)
                          })()}
                        </div>

                        {/* Footer Preview */}
                        {templateForm.footerText && (
                          <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                            {templateForm.footerText}
                          </div>
                        )}

                        {/* Buttons Preview */}
                        {templateForm.buttons.length > 0 && (
                          <div className="pt-2 flex flex-wrap gap-2">
                            {templateForm.buttons.map((button, index) => (
                              <div
                                key={index}
                                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                              >
                                {button.title}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Advanced Options */}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <h6 className="text-xs font-semibold text-gray-900">
                      Advanced Options
                    </h6>
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">
                        Use the buttons below to save your template locally or
                        submit it to Facebook for approval.
                      </p>
                    </div>
                  </div>

                  {/* Components JSON (Advanced) */}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <details className="space-y-2">
                      <summary className="text-xs font-medium text-gray-900 cursor-pointer">
                        Components JSON (Advanced)
                      </summary>
                      <div className="space-y-1 pt-2">
                        <p className="text-xs text-gray-400">
                          Edit raw components JSON (overrides form values)
                        </p>
                        <textarea
                          rows={4}
                          value={templateForm.componentsJson}
                          onChange={event =>
                            handleTemplateFormChange(
                              'componentsJson',
                              event.target.value
                            )
                          }
                          placeholder='e.g. [{"type":"BODY","text":"Hello {{1}}, welcome!"}]'
                          className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </details>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowTemplateForm(false)
                          setTemplateForm(defaultTemplateForm)
                          setHasCustomComponentsJson(false)
                          setEditingTemplateId(null)
                        }}
                        className="rounded-md border border-blue-500 px-3 py-1 text-xs text-blue-600 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTemplateSubmit(false)}
                        disabled={savingTemplate}
                        className="inline-flex items-center rounded-md bg-gray-600 px-3 py-1 text-xs font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {savingTemplate ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <Plus className="mr-1 h-3 w-3" />
                        )}
                        {editingTemplateId ? 'Save Only' : 'Save Only'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTemplateSubmit(true)}
                        disabled={savingTemplate}
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {savingTemplate ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <Upload className="mr-1 h-3 w-3" />
                        )}
                        {editingTemplateId
                          ? 'Save & Submit to Facebook'
                          : 'Create & Submit to Facebook'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sync Templates Modal */}
        {showSyncModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="max-h-[80vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Templates from Meta Business
                </h3>
                <button
                  type="button"
                  onClick={() => setShowSyncModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto p-6">
                {whatsappTemplates.length === 0 ? (
                  <p className="text-center text-gray-500">
                    No templates found in your Meta Business Account
                  </p>
                ) : (
                  <div className="space-y-4">
                    {whatsappTemplates.map(template => {
                      const bodyComponent = template.components?.find(
                        (c: any) => c.type === 'BODY'
                      )
                      const isImporting = importingTemplates.has(template.name)
                      const existingTemplate = templates.find(
                        t =>
                          t.externalId === template.name ||
                          t.name === template.name
                      )
                      const needsUpdate =
                        existingTemplate &&
                        existingTemplate.isActive !==
                          (template.status === 'APPROVED')

                      return (
                        <div
                          key={template.name}
                          className="rounded-lg border border-gray-200 p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">
                                  {template.name}
                                </h4>
                                <span
                                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                    template.status === 'APPROVED'
                                      ? 'bg-green-100 text-green-800'
                                      : template.status === 'PENDING'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {template.status}
                                </span>
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                  {template.language}
                                </span>
                                {template.category && (
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                    {template.category}
                                  </span>
                                )}
                                {existingTemplate && (
                                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                                    In DB
                                  </span>
                                )}
                              </div>
                              {bodyComponent?.text && (
                                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">
                                  {bodyComponent.text}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleImportTemplate(template)}
                              disabled={isImporting}
                              className={`ml-4 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                                needsUpdate
                                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                                  : existingTemplate
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {isImporting ? (
                                <>
                                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                  {existingTemplate
                                    ? 'Updating...'
                                    : 'Importing...'}
                                </>
                              ) : needsUpdate ? (
                                <>
                                  <RefreshCw className="mr-1 h-4 w-4" />
                                  Update Status
                                </>
                              ) : existingTemplate ? (
                                <>
                                  <RefreshCw className="mr-1 h-4 w-4" />
                                  Re-sync
                                </>
                              ) : (
                                <>
                                  <Download className="mr-1 h-4 w-4" />
                                  Import
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setShowSyncModal(false)}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {contactFormMode === 'create'
                ? 'Create Contact'
                : contactFormMode === 'edit'
                  ? 'Edit Contact'
                  : 'Select a contact'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-500">
                {contactFormMode === 'list'
                  ? 'Select a saved contact or add a new one.'
                  : contactFormMode === 'create'
                    ? 'Add a new person to your address book.'
                    : `Update contact details for ${contactBeingEdited ? getContactDisplayName(contactBeingEdited) : 'this person'}.`}
              </p>
              {contactFormMode === 'list' ? (
                <Button size="sm" onClick={startCreateContact}>
                  <Plus className="mr-1 h-4 w-4" />
                  New contact
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={resetContactForm}
                  disabled={isSubmittingContact}
                >
                  Back to list
                </Button>
              )}
            </div>

            {contactFormMode === 'list' ? (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={contactSearchTerm}
                    onChange={event => setContactSearchTerm(event.target.value)}
                    placeholder="Search contacts by name, email, or phone number"
                    className="pl-9"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {CONTACT_FILTERS.map(filter => {
                    const isActive = contactFilters[filter.key]
                    const Icon = filter.icon
                    return (
                      <button
                        key={filter.key}
                        type="button"
                        onClick={() => toggleContactFilter(filter.key)}
                        className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          isActive
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span>{filter.label}</span>
                      </button>
                    )
                  })}
                </div>
                {addressBookError instanceof Error && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {addressBookError.message}
                  </div>
                )}
                <div className="max-h-80 overflow-y-auto space-y-2">
                  {loadingAddressBook ? (
                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading contacts...
                    </div>
                  ) : filteredAddressBookContacts.length === 0 ? (
                    <div className="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                      No contacts match your filters.
                    </div>
                  ) : (
                    filteredAddressBookContacts.map(contact => {
                      const availableChannels = CONTACT_FILTERS.filter(filter =>
                        contactHasField(contact, filter.field)
                      )
                      const phoneValue =
                        contact.phoneNumber === null ||
                        contact.phoneNumber === undefined
                          ? ''
                          : String(contact.phoneNumber)
                      const isDeleting =
                        deleteContactMutation.isPending &&
                        deleteContactMutation.variables === contact.id

                      return (
                        <div
                          key={contact.id}
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            handleSelectContactFromAddressBook(contact)
                          }
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              handleSelectContactFromAddressBook(contact)
                            }
                          }}
                          className="rounded-md border border-gray-200 px-3 py-3 transition-colors hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <p className="font-medium text-gray-900">
                                {getContactDisplayName(contact)}
                              </p>
                              {contact.title && (
                                <p className="text-xs text-gray-500">
                                  {contact.title}
                                </p>
                              )}
                              {contact.email && (
                                <p className="text-xs text-gray-500">
                                  {contact.email}
                                </p>
                              )}
                              <p className="font-mono text-sm text-gray-600">
                                {phoneValue || 'No phone'}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={event => {
                                  event.stopPropagation()
                                  startEditContact(contact)
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {isSupervisor && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={event => {
                                    event.stopPropagation()
                                    handleDeleteContact(contact)
                                  }}
                                  disabled={isDeleting}
                                  aria-disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                          {availableChannels.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {availableChannels.map(filter => {
                                const ChannelIcon = filter.icon
                                return (
                                  <Badge
                                    key={`${contact.id}-${filter.key}`}
                                    variant="outline"
                                    className={filter.badgeStyles}
                                  >
                                    <ChannelIcon className="h-3 w-3" />
                                    <span>{filter.label}</span>
                                  </Badge>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              </>
            ) : (
              <UnifiedContactForm
                key={`contact-form-${contactFormMode}-${contactBeingEdited?.id || 'new'}`}
                initialData={contactFormValues}
                onSubmit={handleContactFormSubmit}
                onCancel={resetContactForm}
                isLoading={isSubmittingContact}
                mode={contactFormMode}
                variant="inline"
                errors={contactFormErrors}
                formError={contactFormSubmissionError}
                submitButtonText={
                  isSubmittingContact
                    ? 'Saving...'
                    : contactFormMode === 'create'
                      ? 'Create Contact'
                      : 'Save Changes'
                }
                cancelButtonText="Cancel"
                showSubmitButton={true}
                showLoadingSpinner={true}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default OutboundMessaging
